import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  OFFICE_QUESTS,
  SOUND_REACTIONS,
  WORLD_THEMES,
  buildRoomPath,
  ENTRY_DOORWAY_ID,
  LEVELS,
  SEAT_INTERACT_RANGE,
  zoneAtIn,
  groupOccupantsByRoom,
  screenshareMaxBitrate,
  MAX_CONCURRENT_SCREENSHARES_PER_ROOM,
  type Animation,
  type EquippedCosmetics,
  type InteractiveObject,
  type FurnitureItem,
  type InventoryDto,
  type Level,
  type LevelId,
  type OfficeQuestProgressView,
  type PresenceFlavor,
  type MediaControlMessage,
  type MediaHubStateView,
  type Reaction,
  type RpsChoice,
  BARK_MAX_SAMPLES,
  BARK_SAMPLE_INTERVAL_MS,
  barkPointsFromLevel,
  type Seat,
  type SoundReactionId,
  type Weather,
  type WorldEventKind,
  type WorldThemeId,
} from '@duck/contracts';
import { config } from '../../config';
import { api, ApiError } from '../../lib/api';
import { queryClient } from '../../lib/queryClient';
import { isTabHidden } from '../../lib/tab-visibility';
import { useAuthStore } from '../../state/auth.store';
import { RealtimeClient, type ConnectionStatus } from '../../net/realtime-client';
import { physicalKey } from '../../net/keyboard';
import {
  VoiceClient,
  type AudiblePeer,
  type CameraStatus,
  type CurrentZone,
  type MicDevice,
  type VoiceStatus,
} from '../../net/voice-client';
import {
  OfficeEngine,
  bareLevelFor,
  type EntryActionView,
  type EntryActionKind,
  type DoorActionView,
  type ContextMenuRequest,
} from './render/office-engine';
import { FurnitureContextMenu } from './FurnitureContextMenu';
import { nameColorCss } from './render/species';
import { WelcomeModal } from './WelcomeModal';
import { PinnedCameraCard } from './camera/PinnedCameraCard';
import { SelfCameraPreview } from './camera/SelfCameraPreview';
import { CameraPrimer, readCameraPrimerSeen, markCameraPrimerSeen } from './camera/CameraPrimer';
import {
  playSoundReaction,
  prefetchSoundReaction,
  soundReactionDebug,
  unlockSoundAudio,
} from './sound/soundboard-audio';
import { playDoorbell, playKnock, playDoorToggle, unlockEntrywayAudio, entrywayAudioDebug } from './sound/entryway-audio';
import {
  HUD,
  type ChallengeGame,
  type ChatLine,
  type RosterMember,
  type RosterSection,
  type RpsInvitePrompt,
  type RpsMatchView,
  type RpsResultView,
  type BarkInvitePrompt,
  type BarkMatchView,
  type BarkResultView,
} from './HUD';
import { MediaHubCard } from './radio/MediaHubCard';
import { radioDebug } from './radio/radio-player';
import type { ScreenStatus } from '../../net/voice-client';
import { useApplyAudioSettings } from './AudioSettings';
import { useApplyRenderSettings } from './RenderSettings';
import { effectiveGain, useAudioSettingsStore } from '../../state/audio-settings.store';
import { peerGainRecord, usePeerVolumeStore } from '../../state/peer-volume.store';
import { setMicLevel } from '../../state/mic-level.store';
import { resetBarkScore, setBarkScore } from '../../state/bark-score.store';
import { readRenderTally } from '../../state/render-tally';
import type { GuildPanelTab } from './GuildPanel';

/**
 * How many lines of social chatter (M7a) we keep in the resizable chat window. EPHEMERAL — in-memory
 * only, never persisted/logged (Constitution Art.1: banter, never a surveillance log). Bumped from 5 to
 * a small scrollback so the MMO-style chat window has a little history to breathe; still session-only and
 * cleared on reload.
 */
const MAX_CHAT_LINES = 50;

/** How many nearby faces you can PIN at once (ADR-0030 fast-follow) — a small "huddle", not a Zoom grid. */
const MAX_PINNED_CAMERAS = 3;

/**
 * Resolve the authoritative GEOMETRY level (Phase 3) for a client + cache it in `ref`, so OfficePage's own
 * zone/seat math targets the same world the engine renders. Module-level (not a hook) so the once-mounted
 * dev-hook / poll / E-key effects that call it stay non-reactive (no exhaustive-deps churn).
 */
function resolveLevelInto(ref: { current: Level }, client: RealtimeClient | null): Level {
  return (ref.current = LEVELS[client?.levelId() ?? 'office'] ?? LEVELS.office);
}

/** Nearest seat to a point within `level`'s seat-interact range, or null (level-bound; mirrors `nearestSeat`). */
function nearestSeatIn(level: Level, x: number, y: number): Seat | null {
  let best: Seat | null = null;
  let bestSq = SEAT_INTERACT_RANGE * SEAT_INTERACT_RANGE;
  for (const s of level.seats) {
    const sq = (s.x - x) ** 2 + (s.y - y) ** 2;
    if (sq <= bestSq) {
      bestSq = sq;
      best = s;
    }
  }
  return best;
}

type Status = 'connecting' | 'connected' | 'error';

/**
 * Shallow-equality for a {@link MediaHubStateView} (ADR-0033): only the DISCRETE fields that drive a
 * re-render are compared (the card extrapolates position itself), so a steady hub never re-renders every
 * second. Queue/history compare by id-sequence (the only thing the UI keys on).
 */
function mediaHubEqual(a: MediaHubStateView | null, b: MediaHubStateView | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const idSeq = (xs: { id: string }[]): string => xs.map((x) => x.id).join(',');
  return (
    a.hostSessionId === b.hostSessionId &&
    a.playing === b.playing &&
    a.updatedAt === b.updatedAt &&
    a.epoch === b.epoch &&
    (a.now?.id ?? '') === (b.now?.id ?? '') &&
    (a.now?.ref ?? '') === (b.now?.ref ?? '') &&
    idSeq(a.queue) === idSeq(b.queue) &&
    idSeq(a.history) === idSeq(b.history)
  );
}

export function OfficePage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const tokens = useAuthStore((s) => s.tokens);
  const logoutSession = useAuthStore((s) => s.logout);

  const clientRef = useRef<RealtimeClient | null>(null);
  // The RESOLVED geometry level (Phase 3) for OfficePage's own zone/seat math — kept in sync with the
  // synced `levelId` (via the module-level {@link resolveLevelInto}, called from the lazy ref reads) so
  // prompts (Sit / Work) + the screen-share audience count target the same world the engine renders
  // (office or castle). A REF (not state/callback) so the once-mounted setup effects stay non-reactive.
  const levelRef = useRef<Level>(LEVELS.office);
  // The live Pixi engine instance, published by OfficeView so the voice client can drive the
  // speaking indicator (engine.setSpeaking) without re-creating the engine on every render.
  const engineRef = useRef<OfficeEngine | null>(null);
  const voiceRef = useRef<VoiceClient | null>(null);
  // Reflect the per-device audio mix (effects / voices / radio faders) into the audio engines: SFX + the
  // live voice client here, the radio via RadioCard. Personal, per-device comfort — never shared.
  useApplyAudioSettings(voiceRef);
  // Reflect the per-device render-quality preference (auto/full/lite) onto the engine — a live resolution
  // swap, no reload. Personal, per-device comfort — never shared.
  useApplyRenderSettings(engineRef);
  const [status, setStatus] = useState<Status>('connecting');
  const [error, setError] = useState<string>('');
  const [playerCount, setPlayerCount] = useState(0);
  // M3.5b "Reconnection grace": live connection health for the gentle, non-blocking HUD indicator. A
  // blip / server restart re-claims the same seat transparently (`reconnecting`); only an exhausted
  // grace window surfaces `lost` (calm "reload"). A deliberate leave never reaches `reconnecting`/`lost`.
  const [netStatus, setNetStatus] = useState<ConnectionStatus>('connected');
  const [notice, setNotice] = useState<string | null>(null);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');
  const [voiceMuted, setVoiceMuted] = useState(false);
  // My BROADCASTABLE audio-device presence (Discord-style 🎤/🎧 in the participant list): whether my voice
  // is live (I can hear) and whether my mic is deliberately muted — the manual INTENT, not the transient
  // push-to-talk gate. Mirrored from the voice client's onVoiceState and used both to (a) render my OWN
  // roster row instantly (no schema round-trip) and (b) broadcast to everyone via the realtime client.
  const [myVoice, setMyVoice] = useState<{ voiceOn: boolean; micMuted: boolean }>({
    voiceOn: false,
    micMuted: false,
  });
  // The full session roster grouped by room — the participant list. Recomputed off the authoritative
  // roster at a calm cadence (catches others moving rooms / toggling mic) and immediately on join/leave
  // and my own voice toggle. Derived state, so it never drives the render hot path.
  const [roomSections, setRoomSections] = useState<RosterSection[]>([]);
  // cost-census: signature of the last roster we rendered, to skip the 1Hz re-render when it's unchanged.
  const lastRosterSig = useRef<string>('');

  // Build the grouped, room-sectioned participant list from the authoritative roster. My OWN row uses my
  // LOCAL voice intent (instant, no schema round-trip); everyone else reads their synced voiceOn/micMuted.
  // Members are pre-sorted by name so the pure grouper's per-room order is stable (it only hoists me first).
  const computeRoster = useCallback(() => {
    const c = clientRef.current;
    if (!c) {
      lastRosterSig.current = '';
      setRoomSections([]);
      return;
    }
    const selfId = c.selfSessionId ?? null;
    const members: RosterMember[] = [];
    for (const [sessionId, p] of c.players) {
      const isSelf = sessionId === selfId;
      members.push({
        id: sessionId,
        userId: p.userId,
        x: p.x,
        y: p.y,
        name: p.displayName || 'Duck',
        species: p.species || 'duck',
        duckColor: p.duckColor || 'yellow',
        isSelf,
        voiceOn: isSelf ? myVoice.voiceOn : p.voiceOn === true,
        micMuted: isSelf ? myVoice.micMuted : p.micMuted === true,
      });
    }
    members.sort((a, b) => a.name.localeCompare(b.name));
    const sections = groupOccupantsByRoom(members, selfId);
    // cost-census: the 1Hz poll rebuilt a fresh array every tick → a forced HUD re-render every second even
    // when nothing changed. Bail when the rendered roster is identical. The signature DROPS raw x/y (a duck
    // moving WITHIN a room doesn't change the grouping or display — only a room crossing / name / voice
    // change does), so a static room re-renders 0×/sec and the memoized rows finally short-circuit.
    const sig = JSON.stringify(sections, (k, v) => (k === 'x' || k === 'y' ? undefined : v));
    if (sig === lastRosterSig.current) return;
    lastRosterSig.current = sig;
    setRoomSections(sections);
  }, [myVoice]);

  // Keep the participant list live: recompute on a calm 1 Hz tick (catches others crossing room
  // boundaries / toggling their mic), and immediately whenever the population (join/leave, via
  // playerCount) or my own voice state changes.
  useEffect(() => {
    if (status !== 'connected') {
      lastRosterSig.current = '';
      setRoomSections([]);
      return undefined;
    }
    computeRoster();
    const id = window.setInterval(computeRoster, 1000);
    return () => window.clearInterval(id);
  }, [status, playerCount, computeRoster]);
  // M3.2 zone awareness + M3.3 push-to-talk / accessibility (all client-side UI; server gates audio).
  const [currentZone, setCurrentZone] = useState<CurrentZone | null>(null);
  const [pttEnabled, setPttEnabled] = useState(false);
  // Mic input level (R1): driven ~8 Hz from the voice VAD into the isolated mic-level store (so only
  // the meter re-renders, not this whole tree). NOT React state here.
  const [micDevices, setMicDevices] = useState<MicDevice[]>([]);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [speakerDevices, setSpeakerDevices] = useState<MicDevice[]>([]);
  const [currentSinkId, setCurrentSinkId] = useState<string | null>(null);
  // Webcam "show your face" (ADR-0030): my LOCAL camera lifecycle + my own outgoing stream (for the
  // self-preview); the per-viewer PINNED peer + its live stream (a private UI choice that never touches
  // the server or the pinned person); and the one-time consent primer shown before the first prompt.
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('off');
  const [selfCameraStream, setSelfCameraStream] = useState<MediaStream | null>(null);
  // MULTI-PIN (ADR-0030 fast-follow): up to MAX_PINNED_CAMERAS faces pinned at once (a "huddle"). Per-viewer
  // only — never touches the server or the pinned people. `pinnedStreams` mirrors each pinned peer's live
  // stream so the cards re-render when a stream arrives / changes (a sync effect keeps it ⊆ pinnedSessionIds).
  const [pinnedSessionIds, setPinnedSessionIds] = useState<string[]>([]);
  const [pinnedStreams, setPinnedStreams] = useState<Record<string, MediaStream | null>>({});
  const [showCameraPrimer, setShowCameraPrimer] = useState(false);
  // Mirror the pins into a ref so the once-created voice callbacks read the latest without a stale closure
  // (auto-unpin a face when its stream drops / the peer walks out of range).
  const pinnedRef = useRef<string[]>([]);
  pinnedRef.current = pinnedSessionIds;
  // M3.4 "Block a citizen" (emotion: Trust): the peers I currently HEAR (drives the "People nearby"
  // list) and the STABLE userIds I've BLOCKED (local optimistic set; the SERVER severs the audio both
  // ways). Tracking by userId (not sessionId) keeps a block coherent across the peer reconnecting with
  // a fresh sessionId. Self-protection, never punishment — the blocked party is never shown or notified.
  const [audiblePeers, setAudiblePeers] = useState<AudiblePeer[]>([]);
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(() => new Set());
  // Last-known display name per blocked userId, so the "Blocked" list still reads a real name after the
  // citizen leaves the roster (offline peers aren't in `players`, so their name can't come from there).
  const blockedNamesRef = useRef<Map<string, string>>(new Map());
  // A11y FIX 2 (WCAG 4.1.3): a polite live-region message announcing block/unblock to screen readers.
  // A live region only re-announces when its TEXT changes, so blocking two same-named ducks (or
  // re-doing the same action) could be silently swallowed. `announceVoice` first CLEARS the region,
  // then sets the message on the next frame — guaranteeing a text change so the announcement re-fires.
  const [voiceA11yAnnouncement, setVoiceA11yAnnouncement] = useState('');
  const announceVoice = useCallback((message: string) => {
    setVoiceA11yAnnouncement('');
    requestAnimationFrame(() => setVoiceA11yAnnouncement(message));
  }, []);
  // M4 "Living World": the local player's ELECTED presence flair (a chosen badge, never inferred),
  // and the nearest interactable object (drives the dual-coded "Press E" prompt).
  const [myPresence, setMyPresence] = useState<PresenceFlavor>('none');
  const [nearestObject, setNearestObject] = useState<InteractiveObject | null>(null);
  // Entryway front door (Floorplan v2 / ADR-0022): the nearest front-door action in range (ring the bell /
  // knock / open the door), or null — drives the dual-coded "Press E" front-door prompt. Emitted by the
  // engine off the predicted self position (the same source as the object/seat prompts).
  const [nearestEntryAction, setNearestEntryAction] = useState<EntryActionView | null>(null);
  // Room Doors: the nearest interior-door action in range (open/close the doorway), or null — drives the
  // dual-coded "[E] Open/Close door" prompt; and the whole door state (closed interior doors + entry-door
  // open) so the host's Doors panel reflects it live (including doors others toggle). Both emitted by the
  // engine off authoritative room state.
  const [nearestDoorAction, setNearestDoorAction] = useState<DoorActionView | null>(null);
  const [doorState, setDoorState] = useState<{ closed: string[]; entryOpen: boolean; held: string[] }>({
    closed: [],
    entryOpen: false,
    held: [],
  });
  // The synced GEOMETRY level id (Phase 3) in React state, so the HUD's host Doors panel lists the RESOLVED
  // level's doorways (office or castle). Set when the door state first syncs (state is available by then).
  const [levelId, setLevelId] = useState<LevelId>('office');
  // Task 2 "interactive SIT": the nearest empty SEAT to the local duck (drives the "Sit" prompt), and
  // the seat I'm currently sitting on (or '' standing) — drives "Stand up" + makes E a sit/stand toggle.
  // Read off the authoritative self position at a gentle cadence (not the render hot path); the SERVER
  // owns positions + occupancy and re-checks both, so this is purely the local prompt.
  const [nearestSeatState, setNearestSeatState] = useState<Seat | null>(null);
  const [mySeatedAt, setMySeatedAt] = useState<string>('');
  // Task 2 "HOLD ITEMS": the prop the local duck is holding ('' = empty hands) — drives the HOLD picker's
  // current selection. Optimistically reflected on pick; the schema poll keeps it honest.
  const [myHeldItem, setMyHeldItem] = useState<string>('');
  // Gamified-XP redesign: whether the local duck is seated at a WORKSTATION desk seat right now (drives
  // the repeatable "Work" prompt). Derived from the authoritative `seatedAt` + `isWorkstationSeat` in the
  // same ~5 Hz seat-reconcile loop, so it appears/clears the instant I sit at / leave a desk.
  const [atWorkstation, setAtWorkstation] = useState(false);
  // Gamified-XP redesign: live, room-wide MEETING-ROOM quest progress (seated count + phase + timers),
  // read off the authoritative room state at 1 Hz (coarse + shared, NOT on the render hot path) — drives
  // the shared "meeting forming / in session" HUD bar. A calm idle default until the first patch lands.
  const [meeting, setMeeting] = useState<OfficeQuestProgressView>({
    seated: 0,
    phase: 'idle',
    elapsedSec: 0,
    targetSec: 0,
  });
  // M4.2 "Living World": the shared sky (day clock + weather) read off the authoritative room state at
  // 1 Hz (atmosphere drifts slowly, so this is plenty — NOT on the render hot path) for the HUD chip.
  const [timeOfDay, setTimeOfDay] = useState(0.35);
  const [weather, setWeather] = useState<Weather>('clear');
  // M7b "Play Together": the SHARED random world event active right now (pizza / disco / fire_drill),
  // read off the authoritative room state at 1 Hz (events are coarse + shared, NOT on the render hot
  // path) — drives the HUD's friendly event banner + its polite "an event started" announcement.
  const [worldEvent, setWorldEvent] = useState<WorldEventKind>('none');
  // M8 "Guilds at Scale": the guild's WORLD THEME everyone shares — read off the authoritative room
  // state at 1 Hz (coarse + shared, NOT on the render hot path). Drives the HUD's calm world-NAME chip
  // and the owner picker's current selection; an owner's switch broadcasts on room state so this updates
  // for everyone present. Defaults to the office.
  const [worldThemeId, setWorldThemeId] = useState<WorldThemeId>('office');

  // MEDIA HUB (ADR-0033): the per-ZONE shared screen. The hub for the zone I'm standing in (polled at 1 Hz
  // off the schema like the world theme/meeting), the clock offset for position extrapolation, the now-
  // playing screen-share stream (when the now-item is a screenshare), my OWN screen-share lifecycle (for
  // the Share button + live tell), and the latest screen reaction (a bumping nonce floats one emoji).
  const [mediaHub, setMediaHub] = useState<MediaHubStateView | null>(null);
  const [mediaClockOffsetMs, setMediaClockOffsetMs] = useState(0);
  const [mediaScreenStream, setMediaScreenStream] = useState<MediaStream | null>(null);
  const [localScreenStatus, setLocalScreenStatus] = useState<ScreenStatus>('off');
  const [mediaReaction, setMediaReaction] = useState<{ reaction: string; nonce: number } | null>(null);
  const mediaReactionSeq = useRef(0);
  // Latest hub mirrored into a ref so the (once-registered) voice callbacks read it without a stale closure.
  const mediaHubRef = useRef<MediaHubStateView | null>(null);
  mediaHubRef.current = mediaHub;
  // The zone id I'm in is the key into the hub map — kept in a ref so the 1 Hz poll + reaction filter read
  // the latest without re-subscribing. Mirrors `currentZone` (the voice client's predicted-self zone).
  const currentZoneIdRef = useRef('');
  currentZoneIdRef.current = currentZone?.id ?? '';
  // Remember whether I was the screen-share host of my current hub, so when my share ends (incl. a
  // reconnect teardown) I tell the server to stop the hub item.
  const wasSharingRef = useRef(false);
  // The zone I was standing in when I STARTED screen-sharing (ADR-0033 bugfix). A share is bound to its
  // zone — if I walk OUT of it, my client stops the share at once (so it never follows me into another
  // room to overlap that room's screen). '' when not sharing.
  const screenShareZoneIdRef = useRef('');
  // M5 "Every Release Is a Raid Boss": which guild slide-over panel is open (Quests / Achievements /
  // Leaderboards), or null. One at a time keeps the HUD calm.
  const [guildPanel, setGuildPanel] = useState<GuildPanelTab>(null);
  // M7a "Socialize": the last few lines of social chatter for the accessible aria-live strip — incoming
  // speech + reactions mirrored as readable text (the Pixi bubbles/emoji are invisible to AT). EPHEMERAL:
  // held in memory only, never persisted/logged, capped at MAX_CHAT_LINES (Constitution Art.1 — not a log).
  const [chatLines, setChatLines] = useState<ChatLine[]>([]);
  // Monotonic id for chat-strip keys (so React can key without persisting anything). Lives in a ref so
  // bumping it never re-renders on its own.
  const chatLineSeq = useRef(0);
  const pushChatLine = useCallback((line: Omit<ChatLine, 'id'>) => {
    const id = ++chatLineSeq.current;
    setChatLines((prev) => [...prev, { id, ...line }].slice(-MAX_CHAT_LINES));
  }, []);
  // M7b-2 "Play Together" (emotion: Tradition) — Rock-Paper-Scissors. Server-authoritative: the realtime
  // owns proximity + the outcome; these are purely the LOCAL presentation of a match.
  //   • rpsInvite — a pending invite (an RPS_INVITE arrived) → the focus-managed invite dialog.
  //   • rpsMatch  — the match I'm in (choosing → after I pick, waiting) → the in-match card.
  //   • rpsResult — the last finished match (win/lose/draw/declined/expired) → the warm result card.
  // None is persisted/logged (Constitution Art. 1 — purely social, never a ranking/metric).
  const [rpsInvite, setRpsInvite] = useState<RpsInvitePrompt | null>(null);
  const [rpsMatch, setRpsMatch] = useState<RpsMatchView | null>(null);
  const [rpsResult, setRpsResult] = useState<RpsResultView | null>(null);
  // The player's OWN active match (M7b-2), mirrored from `rpsMatch` into a ref so the dev `__duckRps()`
  // hook — built once inside the connect effect — can read the LIVE match (matchId/phase/opponent)
  // rather than a stale closure value. Lets the e2e read the CHALLENGER's own matchId after RPS_STARTED.
  // Observability only; product logic never reads this ref.
  const rpsMatchRef = useRef<RpsMatchView | null>(null);
  rpsMatchRef.current = rpsMatch;
  // Auto-clear timer for the transient result card, so a stale "gg!" doesn't linger forever.
  const rpsResultTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bark Battle (Challenge game #2 — contracts domain/bark.ts). Same authority split as RPS: the
  // realtime owns proximity, the shared window, the clamped totals, and the outcome; these are purely
  // the LOCAL presentation of my battle. The ~10 Hz score ticks deliberately BYPASS this state (they
  // write the isolated bark-score store — perf-audit R1), so the HUD only re-renders on real phase
  // changes. None of it is persisted/logged (Constitution Art. 1 — shared laughter, never a metric).
  const [barkInvite, setBarkInvite] = useState<BarkInvitePrompt | null>(null);
  const [barkMatch, setBarkMatch] = useState<BarkMatchView | null>(null);
  const [barkResult, setBarkResult] = useState<BarkResultView | null>(null);
  // Mirror for the dev `__duckBark()` hook (same pattern as rpsMatchRef) — observability only.
  const barkMatchRef = useRef<BarkMatchView | null>(null);
  barkMatchRef.current = barkMatch;
  const barkResultTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Dev-only e2e lever: when set (via __duckBark().setLevelOverride), the battle sampler reads THIS
  // level instead of the real mic — Chromium's fake mic is a constant tone, so a deterministic battle
  // needs a deterministic loudness. Never set by product code; the hook only exists in dev builds.
  const barkLevelOverrideRef = useRef<number | null>(null);
  // Observability: how many samples MY sampler actually sent this battle (dev hook + diagnosis of
  // "score lower than expected" — distinguishes a client sampling gap from server-side drops).
  const barkSentCountRef = useRef(0);
  // Gamified-XP redesign: COALESCE QUEST_FIRED toasts so the tiny WORK +1 ticks and the per-minute
  // participation grants never spam the player. We accumulate grants into a short rolling window and
  // flush ONE warm toast per window: a single quest shows "✨ {name} · +{xp} XP"; multiple grants in the
  // window combine into "✨ +{total} XP · {n} quests". The flush also refetches progression so the
  // rising XP is visible. Lives in refs so accumulating never triggers a render on its own.
  const questToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const questToastAcc = useRef<{ xp: number; lastName: string; count: number }>({
    xp: 0,
    lastName: '',
    count: 0,
  });

  const connected = status === 'connected';
  // Per-room progress (ТЗ §7): the wallet/inventory/guild are scoped to the CURRENT room server-side, so
  // every query is KEYED by the room (workspaceId). Entering another room (or a fresh login) reads a
  // DIFFERENT cache key, so (a) there's no flash of the previous room's coins, and (b) a slow in-flight
  // response from the previous room can never overwrite this room's data (it lands on the old key, which
  // nothing observes). The `invalidateQueries({ queryKey: ['progression'] })` calls elsewhere pass the
  // bare prefix, which React Query prefix-matches to the keyed entry, so they keep working unchanged.
  const roomKey = user?.workspaceId ?? null;
  const progression = useQuery({
    queryKey: ['progression', roomKey],
    queryFn: api.progression,
    enabled: connected,
    refetchInterval: 15_000,
  });
  const guild = useQuery({
    queryKey: ['guild', roomKey],
    queryFn: api.guildInfluence,
    enabled: connected,
    refetchInterval: 30_000,
  });
  // Deep-linkable per-room URLs (ADR-0041): the user's own workspace (the ROOM instance) — its human-
  // readable `slug` is the canonical room key in `/{level_type}/{room}`, the `id` is the fallback key.
  // Cached (no refetch loop — a workspace's slug/id never change under a session); the address-bar
  // canonicalizer below reads it once it's available. A failure here never blocks the room — we just
  // can't canonicalize the URL until it loads.
  const workspace = useQuery({
    queryKey: ['workspace'],
    queryFn: api.myWorkspace,
    enabled: connected,
    staleTime: Infinity,
  });

  // Stable so the connect effect (which captures it) never needs to re-run just to refresh it.
  const flashNotice = useCallback((message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice((current) => (current === message ? null : current)), 2800);
  }, []);

  // Gamified-XP redesign: a co-op office quest I took part in just fired (server-attested). We COALESCE
  // into a short rolling window so the tiny WORK +1 ticks + the per-minute participation grants never
  // spam — one warm toast per ~1.2s window, then a single progression refetch so the rising XP is
  // visible. A lone grant reads "✨ {name} · +{xp} XP"; several in the window combine into a calm summary.
  const QUEST_TOAST_WINDOW_MS = 1200;
  const handleQuestFired = useCallback(
    (quest: { quest: keyof typeof OFFICE_QUESTS; xp: number }) => {
      const acc = questToastAcc.current;
      acc.xp += quest.xp;
      acc.count += 1;
      acc.lastName = OFFICE_QUESTS[quest.quest]?.name ?? 'Office quest';
      if (questToastTimer.current) return; // a flush is already armed — keep accumulating into it
      questToastTimer.current = setTimeout(() => {
        questToastTimer.current = null;
        const { xp, lastName, count } = questToastAcc.current;
        questToastAcc.current = { xp: 0, lastName: '', count: 0 };
        if (count <= 0) return;
        flashNotice(
          count === 1 ? `✨ ${lastName} · +${xp} XP` : `✨ +${xp} XP · ${count} quests`,
        );
        // The grant landed server-side — refetch so the Lv/XP chip (reads progression/me) visibly rises.
        void queryClient.invalidateQueries({ queryKey: ['progression'] });
        void queryClient.invalidateQueries({ queryKey: ['guild'] });
      }, QUEST_TOAST_WINDOW_MS);
    },
    [flashNotice],
  );

  // Giving reputation is a SERVER-AUTHORITATIVE gesture: we only tell the room which nearby duck we
  // aimed at. The server re-checks proximity, applies the budget/per-pair cap, persists, and replies
  // via onReputationResult (below) — then broadcasts the celebration itself (see ADR-0007).
  const handleGiveReputation = useCallback((_targetUserId: string, targetSessionId: string) => {
    clientRef.current?.sendGiveReputation(targetSessionId);
  }, []);

  // Using a world object is a DELIBERATE action (M4): we name only the object id; the server
  // re-checks proximity, applies the effect (elected presence + one-shot animation), and broadcasts
  // it. Never inferred surveillance — the citizen chose to walk over and use it.
  const handleInteract = useCallback((objectId: string) => {
    clientRef.current?.interact(objectId);
  }, []);

  // The engine reports the nearest interactable to the local duck (or null) → drives the HUD prompt.
  const handleNearestObjectChange = useCallback((object: InteractiveObject | null) => {
    setNearestObject(object);
  }, []);

  // Entryway front door (ADR-0022): the engine reports the nearest front-door action (or null) → HUD prompt.
  const handleNearestEntryActionChange = useCallback((action: EntryActionView | null) => {
    setNearestEntryAction(action);
  }, []);

  // Perform a front-door action: ring the bell (whole office hears), knock (only nearby hear), or open the
  // door (host/insider). The SERVER re-checks position/side/host, so it's always safe to send; the resulting
  // sound / door-state change is the feedback. We unlock the entryway AudioContext off this gesture so the
  // chime/knock the server echoes back is audible without a separate "enable sound" step (autoplay policy).
  const handleEntryAction = useCallback((kind: EntryActionKind) => {
    unlockEntrywayAudio();
    const c = clientRef.current;
    if (kind === 'ring') c?.ringBell();
    else if (kind === 'knock') c?.knock();
    else c?.openDoor();
  }, []);

  // Accessible alternative to the in-world E keypress for the front door: the engine owns the predicted
  // position, so it resolves + performs the nearest entry action (which then routes through handleEntryAction).
  const handleEntryActionNearest = useCallback(() => {
    engineRef.current?.triggerEntryAction();
  }, []);

  // Interior room doors (Room Doors): the engine reports the nearest door action (or null) → HUD prompt,
  // and the whole door state on change → the host's Doors panel.
  const handleNearestDoorActionChange = useCallback((action: DoorActionView | null) => {
    setNearestDoorAction(action);
  }, []);
  const handleDoorsChange = useCallback(
    (closedDoorIds: string[], entryDoorOpen: boolean, heldDoorIds: string[]) => {
      setDoorState({ closed: closedDoorIds, entryOpen: entryDoorOpen, held: heldDoorIds });
      // Keep the React-side level id in sync (Phase 3) so the host Doors panel lists the right world's doors.
      const id = clientRef.current?.levelId() ?? 'office';
      setLevelId((prev) => (prev === id ? prev : id));
    },
    [],
  );
  // Toggle an interior door (open/close). Fire-and-forget — the server re-checks proximity/host + the
  // door's HOLD (ADR-0060) and the truth comes back via the synced `closedDoors` map (never an optimistic
  // local mutation). A locked door gets NO local swing sound — the server answers with a KNOCKED broadcast
  // (the knock is the feedback, for both sides of the door).
  const handleDoorAction = useCallback((doorwayId: string, open: boolean, locked?: boolean) => {
    unlockEntrywayAudio();
    if (!locked) playDoorToggle(open); // local feedback; the synced leaf swing is the cue everyone else sees
    clientRef.current?.toggleDoor(doorwayId, open);
  }, []);
  // Accessible alternative to the in-world E keypress for interior doors (the engine owns the position).
  const handleDoorActionNearest = useCallback(() => {
    engineRef.current?.triggerDoorAction();
  }, []);
  // The host's Doors panel toggling ANY door from anywhere: interior doors via toggleDoor, the entry
  // vestibule door via openDoor(open) (the server re-checks host on close). Always safe to send.
  const handleHostToggleDoor = useCallback((doorwayId: string, open: boolean) => {
    unlockEntrywayAudio();
    playDoorToggle(open);
    if (doorwayId === ENTRY_DOORWAY_ID) clientRef.current?.openDoor(open);
    else clientRef.current?.toggleDoor(doorwayId, open);
  }, []);

  // Voice is opt-in and gesture-gated: getUserMedia + AudioContext.resume() must happen inside the
  // click handler (browser autoplay/permission policy). start() is idempotent and fails gracefully.
  const handleEnableVoice = useCallback(() => {
    void voiceRef.current?.start();
  }, []);

  const handleToggleMute = useCallback(() => {
    voiceRef.current?.toggleMute();
  }, []);

  // Webcam toggle (ADR-0030). Turning ON shows the one-time consent primer before the browser prompt,
  // then enables; turning OFF is the instant, no-confirm kill switch. Gesture-gated like the mic.
  const handleToggleCamera = useCallback(() => {
    const vc = voiceRef.current;
    if (!vc) return;
    const status = vc.getCameraStatus();
    if (status === 'on' || status === 'requesting') {
      vc.disableCamera();
      return;
    }
    if (readCameraPrimerSeen()) {
      void vc.enableCamera();
      return;
    }
    setShowCameraPrimer(true);
  }, []);

  const handleConfirmCameraPrimer = useCallback(() => {
    markCameraPrimerSeen();
    setShowCameraPrimer(false);
    void voiceRef.current?.enableCamera();
  }, []);

  // Pin a peer's camera into the top-left media slot (per-viewer only — never touches the server or the
  // pinned person). Tapping a peer's bubble toggles their pin; a NEW pin past the cap drops the OLDEST.
  // Self is never pinnable (you have the self-preview).
  const handlePinCamera = useCallback((sessionId: string | null) => {
    if (!sessionId || sessionId === clientRef.current?.selfSessionId) return;
    setPinnedSessionIds((cur) =>
      cur.includes(sessionId)
        ? cur.filter((s) => s !== sessionId)
        : [...cur, sessionId].slice(-MAX_PINNED_CAMERAS),
    );
  }, []);

  const handleUnpinCamera = useCallback((sessionId: string) => {
    setPinnedSessionIds((cur) => cur.filter((s) => s !== sessionId));
  }, []);

  // Keep `pinnedStreams` exactly in step with `pinnedSessionIds`: seed a newly-pinned peer's stream from
  // whatever the voice client holds now, keep existing ones, drop unpinned peers. onPeerVideoChange below
  // refreshes a live pin's stream and auto-unpins it when the stream goes away.
  useEffect(() => {
    setPinnedStreams((prev) => {
      const next: Record<string, MediaStream | null> = {};
      for (const sid of pinnedSessionIds) {
        next[sid] = prev[sid] ?? voiceRef.current?.getPeerVideoStream(sid) ?? null;
      }
      return next;
    });
  }, [pinnedSessionIds]);

  // Esc as a global camera kill switch (ADR-0030 fast-follow) — guarded so it NEVER hijacks Esc when
  // something else owns it: no-op if the camera is off, if a modal / open menu / disclosure is up (those
  // close on Esc), or if focus is in a text field. Pressing Esc to close a menu never also stops the camera.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const vc = voiceRef.current;
      if (!vc || !vc.isCameraOn()) return;
      if (typeof document !== 'undefined') {
        if (document.querySelector('[aria-modal="true"], [aria-expanded="true"]')) return;
        const ae = document.activeElement as HTMLElement | null;
        if (
          ae?.closest('input, textarea, [contenteditable="true"], [role="menu"], [role="dialog"], [role="listbox"]')
        ) {
          return;
        }
      }
      vc.disableCamera();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Voice auto-starts on join (no fresh user gesture), so the spatial-audio AudioContext can come up
  // `suspended` under the browser autoplay policy — peers would be silent until the player interacts.
  // Resume it on the first real gesture (pointer / key) so everyone is audible the moment the player
  // does anything. Once-only; self-removes after firing.
  useEffect(() => {
    const resume = () => voiceRef.current?.resumeAudio();
    const opts = { once: true, passive: true } as const;
    window.addEventListener('pointerdown', resume, opts);
    window.addEventListener('keydown', resume, opts);
    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
    };
  }, []);

  // Push-to-talk (M3.3): default off (open-mic). When on, the mic stays muted unless V is held.
  const handleTogglePtt = useCallback(() => {
    voiceRef.current?.togglePtt();
  }, []);

  // Switch the active microphone and pre-select it (M3.3 accessibility). Re-sync from the client after
  // the swap: if the picked device vanished between enumeration and selection, the client falls back to
  // the system default — so the picker must reflect what was ACTUALLY acquired, not the failed pick.
  const handleSelectMic = useCallback((deviceId: string) => {
    setCurrentDeviceId(deviceId);
    void voiceRef.current?.setMicrophone(deviceId).then(() => {
      setCurrentDeviceId(voiceRef.current?.getCurrentDeviceId() ?? null);
    });
  }, []);

  // Switch where voice plays back (speaker / headphones) and pre-select it. Persisted by the client.
  // Re-sync afterwards for the same reason as the mic: a vanished device falls back to default.
  const handleSelectSpeaker = useCallback((deviceId: string) => {
    setCurrentSinkId(deviceId);
    void voiceRef.current?.setSpeaker(deviceId).then(() => {
      setCurrentSinkId(voiceRef.current?.getCurrentSinkId() ?? null);
    });
  }, []);

  // Block a citizen's voice (M3.4 — self-protection, never punishment). The VoiceClient resolves the
  // sessionId to a STABLE userId, records it optimistically (so the UI reflects it instantly, even
  // after the server closes the consumer) and tells the server, which AUTHORITATIVELY severs the audio
  // link both ways. onBlockedChange below keeps blockedUserIds in sync; the blocked party is never shown
  // or notified (Constitution Art.1). We ALSO announce it to screen readers (FIX 2, WCAG 4.1.3) —
  // "only you can see this" reassures the action is private — using the name from the current roster.
  const handleBlockCitizen = useCallback(
    (sessionId: string) => {
      const name = clientRef.current?.players.get(sessionId)?.displayName ?? 'this duck';
      voiceRef.current?.blockCitizen(sessionId);
      announceVoice(`Voice from ${name} blocked — only you can see this`);
    },
    [announceVoice],
  );

  // Undo a block (M3.4): the server restores the audio link both ways and the peer becomes audible
  // again once back in range. Also announced politely to screen readers (FIX 2, WCAG 4.1.3).
  const handleUnblockCitizen = useCallback(
    (sessionId: string) => {
      const name = clientRef.current?.players.get(sessionId)?.displayName ?? 'this duck';
      voiceRef.current?.unblockCitizen(sessionId);
      announceVoice(`Unblocked ${name}`);
    },
    [announceVoice],
  );

  // Elect (or clear, with 'none') the presence flair shown over the local duck (M4). Opt-in
  // self-expression — the server validates the flavor and syncs `presence` to everyone. We
  // optimistically reflect it so the picker feels instant; the schema poll keeps it honest.
  const handleSetPresence = useCallback((flavor: PresenceFlavor) => {
    clientRef.current?.setPresence(flavor);
    setMyPresence(flavor);
  }, []);

  // Use the object nearest the local duck — the accessible alternative to the in-world E keypress
  // (M4 a11y). The engine owns the predicted position, so it picks the target; the server re-checks
  // proximity and applies the elected presence + one-shot animation.
  const handleInteractNearest = useCallback(() => {
    engineRef.current?.interactNearest();
  }, []);

  // Task 2 "interactive SIT": sit on / stand up from the nearest seat (the accessible alternative to the
  // in-world E keypress). A server-authoritative TOGGLE: if I'm seated we send my own seat (→ stand); else
  // we resolve the nearest seat off the authoritative self position and send it. The server re-checks
  // proximity + occupancy, snaps + locks me on the seat (or stands me), and syncs `seatedAt` to everyone.
  const handleSitNearest = useCallback(() => {
    const self = clientRef.current?.self();
    if (!self) return;
    if (self.seatedAt) {
      clientRef.current?.sit(self.seatedAt); // toggle: stand up
      return;
    }
    const lvl = resolveLevelInto(levelRef, clientRef.current);
    // bare office: seats are editable furniture (handled by the furniture path), so no static-seat sit-by-key.
    const seat = bareLevelFor(lvl.id) ? null : nearestSeatIn(lvl, self.x, self.y);
    if (seat) clientRef.current?.sit(seat.id);
  }, []);

  // Task 2 "HOLD ITEMS": hold (or empty, with '') a fun prop. We send only the catalog id; the server
  // validates it against the closed catalog + syncs `heldItem` to everyone. Optimistically reflect it so
  // the picker feels instant; the schema poll keeps it honest.
  const handleHoldItem = useCallback((item: string) => {
    clientRef.current?.setHeld(item);
    setMyHeldItem(item);
  }, []);

  // M6 "Express Yourself": after the citizen equips/unequips in the Wardrobe, broadcast the new look
  // over the realtime room so EVERYONE sees it. The gateway already persisted the equipped set + checked
  // ownership; the realtime carries only the (cosmetic-only) display. Mirrors how setPresence broadcasts.
  const handleLookChange = useCallback((equipped: EquippedCosmetics) => {
    clientRef.current?.sendCosmetics(equipped);
  }, []);

  // M7a "Socialize": say a short line / send a quick reaction. We send only the intent; the server
  // trims/validates/rate-limits and echoes a SAID/REACTED broadcast back to EVERYONE (including us), so
  // the bubble/emoji + the accessible strip render uniformly off one path (no optimistic local echo).
  const handleSay = useCallback((text: string) => {
    clientRef.current?.sendSay(text);
  }, []);
  const handleReact = useCallback((reaction: Reaction) => {
    clientRef.current?.sendReact(reaction);
  }, []);

  // Task 6b "Sound reactions": the player picked a soundboard sound. Like say/react we send only the
  // intent; the server validates + rate-limits (the emote limiter) and echoes a SOUND_REACTED back to
  // EVERYONE in range INCLUDING us, so the clip plays uniformly off one path (no optimistic local echo).
  // We resume the AudioContext on THIS click (a user gesture) so the autoplay policy is satisfied and the
  // round-tripped sound is audible the first time.
  const handleSoundReact = useCallback((sound: SoundReactionId) => {
    unlockSoundAudio();
    clientRef.current?.sendSoundReact(sound);
  }, []);

  // MEDIA HUB (ADR-0033): fire-and-forget a media-hub control. Playback control is honoured server-side
  // only for the zone HOST / guild OWNER — others are silently dropped, so this is always safe to call;
  // enqueue/react/sync/claimHost are open to everyone. The server derives the hub from my position; the
  // truth comes back on the schema delta (the 1 Hz poll refreshes `mediaHub`), never an optimistic mutation.
  const handleMediaControl = useCallback((msg: MediaControlMessage) => {
    clientRef.current?.sendMedia(msg);
  }, []);

  // How many OTHER ducks currently share my zone — the screen-share AUDIENCE (ADR-0034). Counted from the
  // authoritative roster + the same `zoneAt` the server uses, so the adaptive bitrate matches who'll
  // actually consume the stream. Excludes me (the host doesn't consume their own producer).
  const zoneViewerCount = useCallback((): number => {
    const c = clientRef.current;
    const zoneId = currentZoneIdRef.current;
    if (!c || !zoneId) return 0;
    let n = 0;
    for (const [sid, p] of c.players) {
      if (sid === c.selfSessionId) continue;
      if ((zoneAtIn(p.x, p.y, resolveLevelInto(levelRef, clientRef.current).zones)?.id ?? '') === zoneId) n++;
    }
    return n;
  }, []);

  // Toggle MY screen share (ADR-0033/0034). The browser picker / stop is driven here; the resulting status
  // transition (onScreenStatus) flips the HUB's now-item via start/stopScreenshare, so the SFU producer and
  // synced state stay coupled. Before starting: (1) PRE-GATE the per-room concurrent-share cap so the host
  // gets instant feedback instead of opening the OS picker for nothing; (2) pick an ADAPTIVE start bitrate
  // from the current audience size. Claiming the host seat first ensures the start is honoured.
  const handleToggleShare = useCallback(() => {
    const voice = voiceRef.current;
    if (!voice) return;
    if (voice.getScreenStatus() === 'on' || voice.getScreenStatus() === 'requesting') {
      voice.disableScreenshare();
      return;
    }
    const c = clientRef.current;
    const myHubIsScreen = (mediaHubRef.current?.now?.kind ?? '') === 'screenshare';
    if (!myHubIsScreen && (c?.mediaScreenshareCount() ?? 0) >= MAX_CONCURRENT_SCREENSHARES_PER_ROOM) {
      flashNotice(`Only ${MAX_CONCURRENT_SCREENSHARES_PER_ROOM} screen shares at once in the office — try again soon 🖥️`);
      return;
    }
    c?.sendMedia({ action: 'claimHost' });
    void voice.enableScreenshare(screenshareMaxBitrate(zoneViewerCount()));
  }, [flashNotice, zoneViewerCount]);

  // Unlock the soundboard AudioContext on the player's FIRST gesture anywhere (a passive arrival who
  // hasn't yet sent their own sound would otherwise have a suspended context and silently miss the
  // room's shared SOUND_REACTED moments until they first interact). One-shot, then self-removes; keeps
  // the no-delayed-blast behaviour (we only resume, we don't replay a missed sound).
  useEffect(() => {
    const unlock = (): void => {
      unlockSoundAudio();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // M7b-2 "Play Together": the engine picked a nearby target for a minigame challenge (off the
  // predicted self position, like give-reputation) → send the matching challenge message. The realtime
  // re-checks proximity authoritatively; the server creates the match + invites the target.
  const handleEngineChallenge = useCallback(
    (_targetUserId: string, targetSessionId: string, game: ChallengeGame) => {
      if (game === 'bark') clientRef.current?.sendBarkChallenge(targetSessionId);
      else clientRef.current?.sendRpsChallenge(targetSessionId);
    },
    [],
  );

  // M7b-2: the HUD "Challenge" picker chose a game. The engine owns the predicted self position, so it
  // picks the nearest other duck (same proximity logic give-reputation uses) and fires
  // handleEngineChallenge to send. A gentle notice gives the challenger feedback even before the server
  // prompts (the contract sends the matchId via the invite/pending messages, never an ack). If no duck
  // is in range, nudge the player to wander closer — kind, never an error.
  const handleChallengeNearest = useCallback(
    (game: ChallengeGame) => {
      const targetSid = engineRef.current?.challengeNearest(game) ?? null;
      if (targetSid) {
        const name = clientRef.current?.players.get(targetSid)?.displayName ?? 'a duck';
        flashNotice(
          game === 'bark'
            ? `🐶 Challenged ${name} to a Bark Battle!`
            : `🪨📄✂️ Challenged ${name} to Rock-Paper-Scissors!`,
        );
      } else {
        flashNotice('Wander a little closer to a duck to challenge them 🦆');
      }
    },
    [flashNotice],
  );

  // Submit my choice in the active match (M7b-2). The server resolves once both players have picked. I
  // flip my local view to "waiting" so the choice buttons disable and I get a "waiting for…" state.
  // Works for BOTH surfaces: the invite dialog (accepting by picking) and the in-match card (challenger).
  const handleRpsChoose = useCallback((matchId: string, choice: RpsChoice) => {
    clientRef.current?.sendRpsChoose(matchId, choice);
    // Resolve the opponent name from whichever surface is live, so the waiting copy reads naturally.
    setRpsInvite((inv) => {
      if (inv && inv.matchId === matchId) {
        setRpsMatch({ matchId, opponentName: inv.fromName, phase: 'waiting', myChoice: choice });
        return null; // close the invite dialog — I've accepted by choosing
      }
      return inv;
    });
    setRpsMatch((m) =>
      m && m.matchId === matchId ? { ...m, phase: 'waiting', myChoice: choice } : m,
    );
  }, []);

  // Decline an invite (M7b-2) — a gentle "no thanks", never a penalty. Tell the server + close the
  // prompt locally so the world is calm again immediately.
  const handleRpsDecline = useCallback((matchId: string) => {
    clientRef.current?.sendRpsDecline(matchId);
    setRpsInvite((inv) => (inv && inv.matchId === matchId ? null : inv));
    setRpsMatch((m) => (m && m.matchId === matchId ? null : m));
  }, []);

  // Dismiss the transient result card (M7b-2) — the "gg!" was seen.
  const handleDismissRpsResult = useCallback(() => {
    if (rpsResultTimer.current) clearTimeout(rpsResultTimer.current);
    rpsResultTimer.current = null;
    setRpsResult(null);
  }, []);

  // Accept a Bark Battle invite (Challenge game #2). Accepting IS the consent to be heard barking, so
  // a manually-muted mic is unmuted as part of the gesture (PTT stays PTT — holding the key is the
  // bark). The server replies BARK_START (the shared countdown) — until it lands we show an optimistic
  // "waiting" card; a dead invite resolves via BARK_RESULT ('expired'), which clears it.
  const handleBarkAccept = useCallback((matchId: string) => {
    clientRef.current?.sendBarkAccept(matchId);
    // Accepting a bark battle IS consent to be heard, so the accept click (a real user gesture, which
    // browser autoplay/permission policy requires) both brings voice LIVE if it isn't — `start()` is
    // idempotent + gesture-gated — and clears a manual mute. Without the `start()` a never-enabled duck
    // would bark into a dead channel and lose 0-score; the countdown card's fix-it nudge stays as a
    // fallback if permission is denied.
    void voiceRef.current?.start();
    voiceRef.current?.unmute();
    // Resolve the opponent name from the live invite (same nested-updater pattern as handleRpsChoose),
    // closing the dialog and opening the optimistic "waiting" card in one swap.
    setBarkInvite((inv) => {
      if (inv && inv.matchId === matchId) {
        setBarkMatch({
          matchId,
          opponentName: inv.fromName,
          phase: 'waiting',
          countdownMs: 0,
          durationMs: 0,
        });
        return null;
      }
      return inv;
    });
  }, []);

  // Decline / back out of a Bark Battle — a gentle "no thanks", never a penalty.
  const handleBarkDecline = useCallback((matchId: string) => {
    clientRef.current?.sendBarkDecline(matchId);
    setBarkInvite((inv) => (inv && inv.matchId === matchId ? null : inv));
    setBarkMatch((m) => (m && m.matchId === matchId ? null : m));
  }, []);

  // Dismiss the transient Bark Battle result card — the "gg!" was seen.
  const handleDismissBarkResult = useCallback(() => {
    if (barkResultTimer.current) clearTimeout(barkResultTimer.current);
    barkResultTimer.current = null;
    setBarkResult(null);
  }, []);

  // Bark Battle: flip 'countdown' → 'battle' when the LOCAL 3-2-1 elapses. The server independently
  // opens the authoritative window on its own clock (with a late grace), so this timer only decides
  // when the SAMPLER starts and the card flips — a drifted clock can shave a sample or two, never
  // forge one. Keyed by matchId so a fresh battle re-arms cleanly.
  useEffect(() => {
    if (barkMatch?.phase !== 'countdown') return undefined;
    const { matchId, countdownMs } = barkMatch;
    const timer = setTimeout(() => {
      setBarkMatch((m) => (m && m.matchId === matchId ? { ...m, phase: 'battle' } : m));
    }, countdownMs);
    return () => clearTimeout(timer);
    // barkMatch is intentionally narrowed to its primitives: the 10 Hz ticks never touch this state,
    // and phase/matchId are what actually arm the countdown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barkMatch?.phase, barkMatch?.matchId]);

  // Bark Battle MIC SAMPLER: while MY window is open, read the current mic loudness every
  // BARK_SAMPLE_INTERVAL_MS on the same 0..1 scale the HUD meter shows, map it to points
  // (barkPointsFromLevel — contracts, one source of truth) and stream slot-indexed samples. The SERVER
  // clamps + dedupes every slot and owns the totals — this loop can never mint a score. Muted mic reads
  // 0 (a muted duck can't be heard, so it can't score). Stops itself after the last slot; fully torn
  // down on result/unmount. The dev-only override lever exists for the e2e (fake mics are a constant
  // tone); product code never sets it.
  useEffect(() => {
    if (barkMatch?.phase !== 'battle') return undefined;
    const { matchId } = barkMatch;
    let slot = 0;
    barkSentCountRef.current = 0;
    // CATCH-UP sampling: send every slot that is DUE by wall-clock, not one-per-fire. Under main-
    // thread jank (a heavy Pixi frame, a weak laptop, a background hiccup) setInterval fires late and
    // coalesces — a naive one-sample-per-fire loop would silently under-sample and unfairly LOWER the
    // player's score. Backfilling the missed slots with the CURRENT loudness is fair (the duck was
    // barking through the stall; we just couldn't observe it) and keeps the average wire rate at the
    // nominal 10/s, well inside the server's per-second sample budget.
    const startedAt = performance.now();
    const timer = setInterval(() => {
      const due = Math.min(
        BARK_MAX_SAMPLES,
        Math.floor((performance.now() - startedAt) / BARK_SAMPLE_INTERVAL_MS) + 1,
      );
      const level = barkLevelOverrideRef.current ?? voiceRef.current?.readMicLevelNow() ?? 0;
      const points = barkPointsFromLevel(level);
      while (slot < due) {
        clientRef.current?.sendBarkSample(matchId, slot, points);
        barkSentCountRef.current += 1;
        slot += 1;
      }
      if (slot >= BARK_MAX_SAMPLES) clearInterval(timer);
    }, BARK_SAMPLE_INTERVAL_MS);
    return () => clearInterval(timer);
    // Same narrowing as the countdown effect: phase + matchId are the real triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barkMatch?.phase, barkMatch?.matchId]);

  // M7a photo mode: capture the framed world as a PNG (the engine owns the Pixi canvas + renderer). The
  // extract is async; surface a gentle notice if it couldn't capture. Nothing is uploaded or logged — a
  // user-initiated keepsake only.
  const handleCapturePhoto = useCallback(() => {
    void (async () => {
      const ok = await engineRef.current?.capturePhoto();
      if (ok === false) flashNotice('Couldn’t capture a photo just now');
    })();
  }, [flashNotice]);

  // Furniture Store: the player picked "Buy & Place" — arm the engine's placement ghost for that item.
  // The Store closes itself; clicking the world confirms (→ server buy & place), Esc/right-click cancels.
  const handleArmFurniture = useCallback((item: FurnitureItem) => {
    engineRef.current?.armFurniture(item);
    flashNotice('🪑 Click in your room to place it · Esc to cancel');
  }, [flashNotice]);

  // Furniture/object context menu (right-click / long-press): the engine raises a request with what's
  // under the cursor; the menu routes its actions back to the engine (move/rotate) + client (remove/interact).
  const [ctxMenu, setCtxMenu] = useState<ContextMenuRequest | null>(null);
  const handleCtxTake = useCallback((id: string) => {
    engineRef.current?.startMoveFurniture(id);
    flashNotice('🖐️ Carrying it — click a spot to drop · Esc to cancel');
  }, [flashNotice]);
  const handleCtxRotate = useCallback((id: string) => engineRef.current?.rotateFurniture(id), []);
  const handleCtxRemove = useCallback((id: string) => clientRef.current?.removeFurniture(id), []);
  // Sit on / stand up from a placed sit-able piece (reuses the SIT message; the server toggles + re-checks).
  const handleCtxSit = useCallback((id: string) => clientRef.current?.sit(id), []);

  // Gamified-XP redesign: do a beat of WORK while seated at a desk. We send only the intent
  // (ClientMessage.WORK, empty body); the SERVER re-checks seated-at-a-desk + the cooldown and attests
  // the tiny +1 XP, which lands back as a QUEST_FIRED toast (so there's no client-trusted faucet — the
  // old free "Complete a quest" button is gone). Always safe to call: a request while standing / inside
  // the cooldown is simply dropped server-side.
  const handleWork = useCallback(() => {
    clientRef.current?.sendWork();
  }, []);

  // M5 guild panels: toggle the named panel (clicking the active one closes it) / close any panel.
  const handleToggleGuildPanel = useCallback((tab: Exclude<GuildPanelTab, null>) => {
    setGuildPanel((prev) => (prev === tab ? null : tab));
  }, []);
  const handleCloseGuildPanel = useCallback(() => setGuildPanel(null), []);

  // Shipping a release IS the raid boss — a guild-wide shared win. The gateway already persisted it
  // and lifted guild influence; here we fan the celebration out to EVERY duck by reusing the existing
  // realtime CELEBRATE broadcast (kind 'release'). The Pixi CelebrationBurst already renders 'release'
  // confetti, and onCelebration (below) refreshes everyone's stats. No new realtime messages added.
  const handleShipRelease = useCallback(
    (name: string) => {
      // The realtime CELEBRATE schema caps `text` at 80 chars; a longer name (the API allows 120)
      // would be SILENTLY dropped by the realtime server — no confetti, no onCelebration refresh for
      // anyone. So cap the broadcast text to ≤80 chars (an ellipsis marks the trim) so the raid-boss
      // moment ALWAYS lands. The full name still persists server-side (the gateway already stored it).
      const celebrateText = name.length > 80 ? `${name.slice(0, 79)}…` : name;
      clientRef.current?.sendCelebrate('release', undefined, celebrateText);
      flashNotice(`🚀 Shipped “${name}” — the whole guild celebrates! 🎉`);
      void guild.refetch();
    },
    [flashNotice, guild],
  );

  // M8 "Guilds at Scale" (emotion: Love for the World): the OWNER sets the guild's WORLD THEME. The
  // SAME engine re-skins per guild — the engine is the product, not the office. We PATCH the gateway
  // (which persists it guild-wide); the realtime then broadcasts the new theme on the room state, so the
  // world re-skins LIVE for everyone present (the engine re-skins off its per-frame read; the 1 Hz poll
  // above refreshes the HUD name). Cosmetic + guild-wide, never a capability (Constitution Art. 1).
  // Optimistically reflect the choice so the picker feels instant; the poll keeps it honest. A friendly
  // notice confirms it; a failure (e.g. a not-yet-shipped gateway route) surfaces gently, never a crash.
  const handleSetWorldTheme = useCallback(
    (theme: WorldThemeId) => {
      void (async () => {
        try {
          await api.setWorldTheme({ theme });
          setWorldThemeId(theme);
          const name = WORLD_THEMES[theme].name;
          flashNotice(`🌍 World theme set to ${name} — everyone sees it when they enter`);
        } catch (e) {
          flashNotice(
            e instanceof ApiError ? e.message : 'Couldn’t change the world theme just now',
          );
        }
      })();
    },
    [flashNotice],
  );

  // Establish the realtime connection once we have an authenticated user. Keyed on the STABLE session
  // identity (user id + workspace) — deliberately NOT on the `tokens` object. The access token is only
  // needed at JOIN time (Colyseus authenticates the join handshake; the SDK then reconnects in place by
  // its own reconnection token, never the access token). Re-running this on every ~15-minute access-token
  // refresh used to tear down and rebuild the ENTIRE session — room + voice (mic re-handshake) + a fresh
  // Pixi Application / WebGL context — which read to players as a periodic "session reload" AND churned
  // WebGL contexts (browsers free them lazily, so they accumulate), the root cause of the FPS decaying
  // over a long session. We read the freshest identity + token straight from the store AT connect time.
  const sessionUserId = user?.id ?? null;
  const sessionWorkspaceId = user?.workspaceId ?? null;
  useEffect(() => {
    if (!sessionUserId || !sessionWorkspaceId) return;
    let cancelled = false;
    let client: RealtimeClient | null = null;

    void (async () => {
      try {
        // M8 "Guilds at Scale": fetch the guild's chosen WORLD THEME and pass it on join, so the room
        // skins the same engine for everyone (Office HQ / Orbital Station / Wizard Academy / Cozy Café).
        // Best-effort — a failure here just means we walk in as the default OFFICE; never a broken join.
        // CRITICAL: the FIRST joiner's theme seeds the room's frozen LEVEL geometry server-side (ADR-0054),
        // so the fallback MUST be 'office' — a transient theme-fetch error must never boot an office
        // workspace into castle geometry. The realtime re-broadcasts the theme so an owner switch re-skins live.
        const theme = await api.myWorkspaceTheme().catch(() => 'office' as const);
        if (cancelled) return;
        // Read the LATEST identity + token at the moment we actually join: a token refresh during the
        // theme fetch above must not make us connect with a stale access token.
        const { user: authUser, tokens: authTokens } = useAuthStore.getState();
        if (!authUser || !authTokens) return;
        client = await RealtimeClient.connect({
          url: config.realtimeUrl,
          accessToken: authTokens.accessToken,
          workspaceId: authUser.workspaceId,
          duckColor: authUser.duckColor,
          species: authUser.species,
          theme,
          onRosterChange: () => {
            if (!cancelled && client) setPlayerCount(client.players.size);
          },
          onCelebration: () => {
            // A celebration just happened nearby — refresh the stats so the numbers move with the
            // joy. Reputation changes only for the subject; guild influence changes for everyone,
            // so re-reading both keeps every duck's HUD honest the instant the confetti lands.
            if (cancelled) return;
            void queryClient.invalidateQueries({ queryKey: ['progression'] });
            void queryClient.invalidateQueries({ queryKey: ['guild'] });
            // Coins are earned alongside XP/influence (M6), so refresh the wallet too — the Wardrobe's
            // balance + the HUD coin chip move with the joy.
            void queryClient.invalidateQueries({ queryKey: ['inventory'] });
          },
          // M7a "Socialize": someone said something / sent a reaction (SELF and OTHERS — the server
          // echoes ours back). The Pixi bubble/emoji are spawned in the render loop; here we mirror the
          // event into the accessible "recent chatter" strip as readable text so screen-reader users get
          // it too (canvas text is invisible to AT). Resolve the speaker's name from the live roster;
          // "you" for our own line keeps it natural. EPHEMERAL — pushed to a capped in-memory strip only.
          onSaid: (said) => {
            if (cancelled) return;
            const c = clientRef.current;
            const p = c?.players.get(said.sessionId);
            const name = said.sessionId === c?.selfSessionId ? 'You' : (p?.displayName ?? 'A duck');
            pushChatLine({ kind: 'say', name, color: nameColorCss(p?.duckColor), text: said.text });
          },
          onReacted: (reacted) => {
            if (cancelled) return;
            const c = clientRef.current;
            const p = c?.players.get(reacted.sessionId);
            const name = reacted.sessionId === c?.selfSessionId ? 'You' : (p?.displayName ?? 'A duck');
            pushChatLine({
              kind: 'action',
              name,
              color: nameColorCss(p?.duckColor),
              text: `reacted ${reacted.reaction}`,
            });
          },
          // Task 6b "Sound reactions": someone played a short, shared sound (SELF and OTHERS — the server
          // echoes ours back). Synthesize + play it for EVERYONE (incl. self) so the moment feels shared,
          // and mirror it into the accessible chatter strip as readable text (the sound is invisible to
          // assistive tech). ANTI-GRIEF (Constitution Art.1, FIX 1): unlike a silent floating emoji, a loud
          // ~1s clip is a real intrusion, so we DON'T blindly trust the server broadcast — if the sender is
          // someone I've blocked (the voice-block cornerstone), we SUPPRESS BOTH the audio AND the chatter
          // line (I blocked them → no noise, no log). Resolve sessionId→stable userId via the LIVE roster
          // (ref, never stale) and test the LIVE block set (VoiceClient is the block source-of-truth — read
          // through the ref so this callback, captured once on connect, never sees a stale snapshot). My own
          // sessionId is never blocked, so my sounds always play.
          onSoundReacted: (reacted) => {
            if (cancelled) return;
            const c = clientRef.current;
            const p = c?.players.get(reacted.sessionId);
            const blocked = voiceRef.current?.getBlocked();
            if (p?.userId && blocked?.has(p.userId)) return; // blocked → no audio, no chatter
            void playSoundReaction(reacted.sound); // fire-and-forget: async fetch+decode, never awaited
            const name = reacted.sessionId === c?.selfSessionId ? 'You' : (p?.displayName ?? 'A duck');
            const label =
              SOUND_REACTIONS.find((s) => s.id === reacted.sound)?.label ?? reacted.sound;
            pushChatLine({
              kind: 'action',
              name,
              color: nameColorCss(p?.duckColor),
              text: `played a sound: ${label}`,
              soundId: reacted.sound, // enables the 🔊 replay button on this chat row
            });
          },
          // MEDIA HUB (ADR-0033): someone reacted on a zone's screen (SELF + OTHERS — the server echoes
          // ours back, zone-scoped). Float the emoji only for the hub I'm currently watching (match the
          // zoneId), and mirror it into the accessible chatter strip (the float is invisible to AT).
          onMediaReacted: (reacted) => {
            if (cancelled) return;
            if (reacted.zoneId !== currentZoneIdRef.current) return; // a different room's screen
            const c = clientRef.current;
            const p = c?.players.get(reacted.sessionId);
            const name = reacted.sessionId === c?.selfSessionId ? 'You' : (p?.displayName ?? 'A duck');
            mediaReactionSeq.current += 1;
            setMediaReaction({ reaction: reacted.reaction, nonce: mediaReactionSeq.current });
            pushChatLine({
              kind: 'action',
              name,
              color: nameColorCss(p?.duckColor),
              text: `reacted ${reacted.reaction} on the screen`,
            });
          },
          // Entryway front door (ADR-0022): the bell chime (sent to EVERYONE) + the door knock (sent only to
          // nearby ducks). Both are one-shot audio cues the server pushes; we synthesize + play them for the
          // listener and mirror a readable line into the accessible chatter strip (the sound is AT-invisible).
          // The bell also flashes a warm "someone's at the door!" notice so the whole office notices a guest.
          onBellRang: (bell) => {
            if (cancelled) return;
            playDoorbell();
            const who = bell.name ?? 'Someone';
            flashNotice(`🔔 ${who} is at the door!`);
            pushChatLine({ kind: 'system', text: `🔔 ${who} rang the doorbell` });
          },
          onKnocked: (knock) => {
            if (cancelled) return;
            playKnock();
            const who = knock.name ?? 'Someone';
            pushChatLine({ kind: 'system', text: `✊ ${who} knocked on the door` });
          },
          onReputationResult: (result) => {
            // The server's private verdict on our give-reputation gesture.
            if (cancelled) return;
            if (result.ok) {
              flashNotice(
                typeof result.budgetRemaining === 'number'
                  ? `⭐ Reputation given · ${result.budgetRemaining} left today`
                  : '⭐ Reputation given',
              );
              void queryClient.invalidateQueries({ queryKey: ['progression'] });
            } else {
              flashNotice(result.reason ?? 'Could not give reputation');
            }
          },
          // Furniture Store: the server's private verdict on our buy-&-place. On success the coins were
          // charged + the piece persisted (it appears for everyone via synced state) — refetch the wallet
          // so the coin chip + Store update. On failure ("Not enough coins", "This room is full") nothing
          // was charged; just surface the reason.
          onFurnitureResult: (result) => {
            if (cancelled) return;
            if (result.ok) {
              flashNotice(
                typeof result.coins === 'number'
                  ? `🛋️ Placed · ${result.coins} coins left`
                  : '🛋️ Furniture placed',
              );
              void queryClient.invalidateQueries({ queryKey: ['progression'] });
              void queryClient.invalidateQueries({ queryKey: ['inventory'] });
            } else {
              flashNotice(result.reason ?? 'Could not place furniture');
            }
          },
          // Gamified-XP redesign: a co-op office quest I took part in just fired (WORK beat, meeting,
          // coffee break…). Coalesce into a warm, non-spammy toast + refetch progression so the rising
          // XP is visible. The world-reacts-together confetti for the headline raids rides the separate
          // CELEBRATE 'quest_completed' broadcast (handled by onCelebration above).
          onQuestFired: (quest) => {
            if (cancelled) return;
            handleQuestFired(quest);
          },
          // M7b-2 "Play Together": someone challenged ME to Rock-Paper-Scissors. Pop the friendly,
          // focus-managed invite dialog with the three choices + Decline. Only the latest pending invite
          // is held (a fresh challenge replaces a stale one).
          onRpsInvite: (invite) => {
            if (cancelled) return;
            setRpsInvite({
              matchId: invite.matchId,
              fromSessionId: invite.fromSessionId,
              fromName: invite.fromName,
            });
          },
          // M7b-2: MY challenge created a match — the server minted the matchId + invited my target, and
          // sent THIS back to me (the challenger). Flip into the in-match 'choosing' card so I immediately
          // see the three choice buttons (the target sees the invite dialog instead). Both then pick →
          // server resolves → RPS_RESULT. The HUD's RpsMatchCard 'choosing' branch renders the surface.
          onRpsStarted: (started) => {
            if (cancelled) return;
            setRpsMatch({
              matchId: started.matchId,
              opponentName: started.opponentName,
              phase: 'choosing',
            });
          },
          // M7b-2: a match I'm in resolved or ended. Show the warm result card; a WIN also fires a
          // CELEBRATE burst (reusing the existing celebration path). Then REFETCH progression so the
          // server-side PLAY_MINIGAME grant (coins/XP — granted to BOTH players on a real play) lands on
          // the HUD. declined/expired carry no grant, but we still tidy the match state + show the gentle
          // ending. Clear any open invite/match for this match id so the surfaces don't linger.
          onRpsResult: (result) => {
            if (cancelled) return;
            setRpsInvite((inv) => (inv && inv.matchId === result.matchId ? null : inv));
            setRpsMatch((m) => (m && m.matchId === result.matchId ? null : m));
            setRpsResult({
              matchId: result.matchId,
              outcome: result.outcome,
              opponentName: result.opponentName,
              yourChoice: result.yourChoice,
              theirChoice: result.theirChoice,
            });
            // Auto-clear the transient card after a beat (re-armed each result).
            if (rpsResultTimer.current) clearTimeout(rpsResultTimer.current);
            rpsResultTimer.current = setTimeout(() => {
              setRpsResult((r) => (r && r.matchId === result.matchId ? null : r));
              rpsResultTimer.current = null;
            }, 6000);
            // A win is a little moment of joy — confetti over my own duck (reuses the celebration path).
            if (result.outcome === 'win') {
              clientRef.current?.sendCelebrate('quest_completed');
            }
            // The play was the positive action (granted server-side to both) — refresh the wallet/XP so
            // the HUD moves with it. No grant on declined/expired, but a refetch is harmless + keeps the
            // HUD honest.
            if (result.outcome === 'win' || result.outcome === 'lose' || result.outcome === 'draw') {
              void queryClient.invalidateQueries({ queryKey: ['progression'] });
              void queryClient.invalidateQueries({ queryKey: ['inventory'] });
            }
          },
          // Bark Battle (Challenge game #2): someone challenges ME → the accept/decline prompt (only
          // the latest pending invite is held, like RPS).
          onBarkInvite: (invite) => {
            if (cancelled) return;
            setBarkInvite({
              matchId: invite.matchId,
              fromSessionId: invite.fromSessionId,
              fromName: invite.fromName,
            });
          },
          // MY bark challenge is out — show the "waiting for them…" card with the server-minted matchId.
          onBarkPending: (pending) => {
            if (cancelled) return;
            setBarkMatch({
              matchId: pending.matchId,
              opponentName: pending.opponentName,
              phase: 'waiting',
              countdownMs: 0,
              durationMs: 0,
            });
          },
          // Both ducks on accept: reset the isolated scoreboard and run the LOCAL 3-2-1 — the flip to
          // 'battle' (and the mic sampler) is armed by the countdown effect above. Clears any open
          // invite for the same match (the target's dialog swaps straight into the countdown).
          onBarkStart: (start) => {
            if (cancelled) return;
            resetBarkScore(start.durationMs);
            setBarkInvite((inv) => (inv && inv.matchId === start.matchId ? null : inv));
            setBarkMatch({
              matchId: start.matchId,
              opponentName: start.opponentName,
              phase: 'countdown',
              countdownMs: start.countdownMs,
              durationMs: start.durationMs,
            });
          },
          // ~10 Hz during the window: write the authoritative totals into the ISOLATED store (perf-audit
          // R1 — never through HUD props), so only the scoreboard leaf repaints.
          onBarkTick: (tick) => {
            if (cancelled) return;
            setBarkScore(tick.yourScore, tick.theirScore, tick.remainingMs);
          },
          // A battle I'm in ended. Same warm choreography as RPS: clear the live surfaces, show the
          // transient result, confetti on a win, refetch progression when the window was really played
          // (PLAY_MINIGAME is granted to BOTH — the point is barking together, not winning).
          onBarkResult: (result) => {
            if (cancelled) return;
            setBarkInvite((inv) => (inv && inv.matchId === result.matchId ? null : inv));
            setBarkMatch((m) => (m && m.matchId === result.matchId ? null : m));
            setBarkResult({
              matchId: result.matchId,
              outcome: result.outcome,
              opponentName: result.opponentName,
              yourScore: result.yourScore,
              theirScore: result.theirScore,
            });
            if (barkResultTimer.current) clearTimeout(barkResultTimer.current);
            barkResultTimer.current = setTimeout(() => {
              setBarkResult((r) => (r && r.matchId === result.matchId ? null : r));
              barkResultTimer.current = null;
            }, 8000);
            if (result.outcome === 'win') {
              clientRef.current?.sendCelebrate('quest_completed');
            }
            if (result.outcome === 'win' || result.outcome === 'lose' || result.outcome === 'draw') {
              void queryClient.invalidateQueries({ queryKey: ['progression'] });
              void queryClient.invalidateQueries({ queryKey: ['inventory'] });
            }
          },
          // M3.5b: connection health changed. Drives the gentle "Reconnecting…" / "Connection lost"
          // HUD indicator. The world stays on screen throughout — never a blocking modal.
          onConnectionStatus: (s) => {
            if (!cancelled) setNetStatus(s);
          },
          // M3.5b: the room socket transparently re-claimed our SAME seat after an unexpected drop.
          // Colyseus already resynced state + kept our handlers, so the only thing torn down server-side
          // was our SFU media — re-run the voice handshake (no-op if voice was never enabled).
          onReconnected: () => {
            if (cancelled) return;
            void voiceRef.current?.reinitialize();
            // M6: re-assert my equipped look after a reconnect. Colyseus normally re-claims the SAME
            // seat (so the server PlayerState — including cosmetics — survives), but if the realtime
            // restarted and minted a fresh seat, this re-seeds the look so I'm never silently un-dressed.
            const equipped = queryClient.getQueryData<InventoryDto>([
              'inventory',
              useAuthStore.getState().user?.workspaceId ?? null,
            ])?.equipped;
            if (equipped) clientRef.current?.sendCosmetics(equipped);
          },
        });
        if (cancelled) {
          void client.leave();
          return;
        }
        clientRef.current = client;
        // Build the voice client now (signaling rides this room socket), but DON'T start the mic
        // yet — getUserMedia + AudioContext must follow a user gesture (the HUD mic button).
        voiceRef.current = new VoiceClient(client, {
          onStatus: (s) => {
            if (cancelled) return;
            setVoiceStatus(s);
            // Mic just came up → refresh the device picker (labels are only populated post-permission)
            // and pre-select the device we actually acquired.
            if (s === 'live') {
              const vc = voiceRef.current;
              setCurrentDeviceId(vc?.getCurrentDeviceId() ?? null);
              setCurrentSinkId(vc?.getCurrentSinkId() ?? null);
              void vc?.listMicrophones().then((devices) => {
                if (!cancelled) setMicDevices(devices);
              });
              void vc?.listSpeakers().then((devices) => {
                if (!cancelled) setSpeakerDevices(devices);
              });
            } else if (s === 'idle' || s === 'unsupported') {
              setMicDevices([]);
              setSpeakerDevices([]);
              setMicLevel(0);
            }
          },
          onMuted: (m) => {
            if (!cancelled) setVoiceMuted(m);
          },
          // My audio-device presence changed (voice went live/down, or I toggled mute). Mirror it locally
          // for my own instant roster row AND broadcast it so everyone's 🎤/🎧 indicators update live.
          onVoiceState: (voiceOn, micMuted) => {
            if (cancelled) return;
            setMyVoice({ voiceOn, micMuted });
            clientRef.current?.sendVoiceState(voiceOn, micMuted);
          },
          onZoneChange: (zone) => {
            if (cancelled) return;
            setCurrentZone(zone);
            // A screen share is BOUND to the zone it began in (ADR-0033 bugfix): if I walk OUT of it while
            // sharing, stop at once — so my screen never follows me into another room to overlap its
            // screen. disableScreenshare closes the producers → onScreenStatus('off') clears the hub item.
            const voice = voiceRef.current;
            if (
              voice?.isScreenSharing() &&
              screenShareZoneIdRef.current &&
              (zone?.id ?? '') !== screenShareZoneIdRef.current
            ) {
              voice.disableScreenshare();
              flashNotice('Screen share ended — you left the room 🖥️');
            }
          },
          onPttChange: (enabled) => {
            if (!cancelled) setPttEnabled(enabled);
          },
          onInputLevel: (level) => {
            // R1: write to the isolated mic-level store, NOT React state — only the meter re-renders.
            if (!cancelled) setMicLevel(level);
          },
          // M3.4: the peers I currently hear changed (someone became audible / dropped) → refresh the
          // "People nearby" list. Coarse membership signal, never the per-frame gain (no hot-path renders).
          onAudibleChange: (peers) => {
            if (!cancelled) setAudiblePeers(peers);
          },
          // M3.4: my local optimistic blocked set (stable userIds) changed → keep the HUD's "Blocked"
          // section honest. Remember each blocked peer's CURRENT display name so the list still reads a
          // real name after they leave the roster (offline peers aren't in `players`).
          onBlockedChange: (userIds) => {
            if (cancelled) return;
            const c = clientRef.current;
            if (c) {
              for (const [, p] of c.players) {
                if (userIds.has(p.userId)) blockedNamesRef.current.set(p.userId, p.displayName);
              }
              // Forget names for userIds no longer blocked (keeps the cache from growing unbounded).
              for (const uid of [...blockedNamesRef.current.keys()]) {
                if (!userIds.has(uid)) blockedNamesRef.current.delete(uid);
              }
            }
            setBlockedUserIds(userIds);
          },
          onSpeakingChange: (ids) => {
            // The server's authoritative active-speaker set → the Pixi speaking indicator.
            engineRef.current?.setSpeaking(ids);
          },
          onSpeakingLevels: (levels) => {
            // Live per-voice loudness (0..1) → the Pixi swell, so a louder shout makes a bigger duck.
            engineRef.current?.setSpeakingLevels(levels);
          },
          // Webcam (ADR-0030): my own camera lifecycle → the HUD button + self-preview, AND push my own
          // outgoing stream to the engine so my duck wears the gold self-bubble (exactly what others see).
          onCameraStatus: (s, selfStream) => {
            if (cancelled) return;
            setCameraStatus(s);
            setSelfCameraStream(selfStream);
            const selfSid = clientRef.current?.selfSessionId;
            if (selfSid) engineRef.current?.setPeerWebcam(selfSid, s === 'on' ? selfStream : null);
          },
          // A nearby peer's face arrived or dropped → route it to their head bubble; keep a live pin fresh
          // and AUTO-UNPIN if the pinned peer's stream goes away (camera off / left range / blocked) so a
          // frozen/stale face is never left pinned.
          onPeerVideoChange: (sessionId, stream) => {
            if (cancelled) return;
            engineRef.current?.setPeerWebcam(sessionId, stream);
            if (pinnedRef.current.includes(sessionId)) {
              if (stream) {
                setPinnedStreams((m) => ({ ...m, [sessionId]: stream }));
              } else {
                // Stream gone (cam off / left range / blocked) → auto-unpin THIS face (the sync effect
                // drops its stream). Never leave a frozen/stale pinned face.
                setPinnedSessionIds((cur) => cur.filter((s) => s !== sessionId));
              }
            }
          },
          // Screen share (ADR-0033): MY own share lifecycle drives the Share button + the live tell, AND
          // couples to the hub — going 'on' starts the hub's screenshare item; an end (incl. a reconnect
          // teardown) stops it, so the synced state never strands a dead frame.
          onScreenStatus: (s, selfStream) => {
            if (cancelled) return;
            setLocalScreenStatus(s);
            if (s === 'on') {
              wasSharingRef.current = true;
              screenShareZoneIdRef.current = currentZoneIdRef.current; // bind the share to this zone
              clientRef.current?.sendMedia({ action: 'startScreenshare' });
              setMediaScreenStream(selfStream); // instant self-preview (I don't consume my own producer)
            } else if (s === 'off' && wasSharingRef.current) {
              wasSharingRef.current = false;
              screenShareZoneIdRef.current = '';
              clientRef.current?.sendMedia({ action: 'stopScreenshare' });
            }
          },
          // A peer's screen arrived (I'm in their zone) or dropped → refresh the now-playing stream if it's
          // the hub's current screenshare. The 1 Hz poll also resolves this, but the callback is instant.
          onScreenShareChange: (sessionId, stream) => {
            if (cancelled) return;
            const now = mediaHubRef.current?.now;
            if (now?.kind === 'screenshare' && now.ref === sessionId) setMediaScreenStream(stream);
          },
        });
        // Seed the listener-side voice volume from the saved audio mix BEFORE start() builds the master
        // node (start() applies the remembered level); the apply hook then tracks any later changes.
        voiceRef.current.setVoiceVolume(effectiveGain(useAudioSettingsStore.getState().voice));
        // Seed the per-participant PERSONAL voice trims from their persisted store too (the apply hook only
        // fires on a later CHANGE, so a fresh VoiceClient would otherwise miss the already-saved trims). The
        // apply hook ALSO fires once on mount with the same rehydrated map — a harmless idempotent re-apply
        // (setPeerGains is a full replace), so this seed just guarantees the trims are live before start().
        voiceRef.current.setPeerGains(peerGainRecord(usePeerVolumeStore.getState().volumes));
        setPlayerCount(client.players.size);
        setNetStatus(client.connectionStatus());
        setStatus('connected');

        // Voice-first social workspace (product requirement): bring the mic + spatial audio up the
        // instant the room is joined, so citizens land already live (unmuted, open-mic) and can hear
        // everyone around them — no extra "enable voice" click. start() is idempotent and degrades
        // gracefully: if the browser denies the mic, the HUD falls back to its "Allow mic" retry, and
        // any suspended AudioContext is unstuck by the first-gesture resume wired below. The "Join the
        // room" click that navigated us here is recent enough that browsers honor the permission prompt.
        if (!cancelled) void voiceRef.current?.start();

        // M6 "Express Yourself": broadcast my gateway-attested equipped look on join, so I walk into
        // the office already wearing what I chose (and everyone sees it). The realtime defaults a fresh
        // seat to NO cosmetics; this seeds it from the durable inventory. Best-effort — a failure here
        // just means an un-decorated duck until the next equip, never a broken join. Also primes the
        // inventory cache so opening the Wardrobe is instant.
        void api
          .cosmeticsInventory()
          .then((inv) => {
            if (cancelled) return;
            queryClient.setQueryData(['inventory', useAuthStore.getState().user?.workspaceId ?? null], inv);
            clientRef.current?.sendCosmetics(inv.equipped);
          })
          .catch(() => undefined);

        // Dev-only observability for the e2e "manual testing agent" (never used by product logic) —
        // mirrors `__duckWorld`/`__duckVoice`. Lets the e2e read connection health and SIMULATE a
        // transient blip (drop()) to exercise the auto-reconnect path WITHOUT a page reload (M3.5b).
        if (import.meta.env?.DEV) {
          // perf-audit M4: dev React re-render tally so perf-load can report renders/sec per component
          // (R1: `hud` stays flat while talking; R2: `rosterRow`/`chatRow` stay flat on unrelated renders).
          (window as unknown as { __duckRenders?: unknown }).__duckRenders = readRenderTally;
          (window as unknown as { __duckNet?: unknown }).__duckNet = {
            get status() {
              return clientRef.current?.connectionStatus() ?? 'lost';
            },
            get reconnects() {
              return clientRef.current?.reconnectAttempts() ?? 0;
            },
            drop: () => clientRef.current?.forceDropForTest(),
          };
          // Lets the e2e re-broadcast an equipped set after a REST-only equip/unequip (M6), exercising
          // the same realtime path the wardrobe UI uses (clientRef.sendCosmetics).
          (window as unknown as { __duckSetCosmetics?: unknown }).__duckSetCosmetics = (
            equipped: EquippedCosmetics,
          ) => clientRef.current?.sendCosmetics(equipped);
          // M7b-2 "Play Together": dev-only observability + drivers for the e2e to run a deterministic
          // RPS match. `lastInvite`/`lastResult` read off the realtime client's stashed messages;
          // challenge/choose/decline route through the SAME client calls the UI uses (server-authoritative
          // throughout). Never used by product logic — observability only.
          (window as unknown as { __duckRps?: unknown }).__duckRps = () => ({
            // The player's OWN active match (matchId/phase/opponent) — set on RPS_STARTED for the
            // challenger and on choose / invite-accept. Lets the e2e read the CHALLENGER's matchId.
            match: rpsMatchRef.current
              ? {
                  matchId: rpsMatchRef.current.matchId,
                  phase: rpsMatchRef.current.phase,
                  opponentName: rpsMatchRef.current.opponentName,
                }
              : null,
            lastInvite: clientRef.current?.lastRpsInvite
              ? {
                  matchId: clientRef.current.lastRpsInvite.matchId,
                  fromSessionId: clientRef.current.lastRpsInvite.fromSessionId,
                  fromName: clientRef.current.lastRpsInvite.fromName,
                }
              : null,
            lastResult: clientRef.current?.lastRpsResult
              ? {
                  matchId: clientRef.current.lastRpsResult.matchId,
                  outcome: clientRef.current.lastRpsResult.outcome,
                  yourChoice: clientRef.current.lastRpsResult.yourChoice ?? null,
                  theirChoice: clientRef.current.lastRpsResult.theirChoice ?? null,
                  opponentName: clientRef.current.lastRpsResult.opponentName,
                }
              : null,
            challenge: (targetSessionId: string) =>
              clientRef.current?.sendRpsChallenge(targetSessionId),
            choose: (matchId: string, choice: RpsChoice) =>
              clientRef.current?.sendRpsChoose(matchId, choice),
            decline: (matchId: string) => clientRef.current?.sendRpsDecline(matchId),
          });
          // Bark Battle (Challenge game #2): dev-only observability + drivers for the e2e to run a
          // DETERMINISTIC battle. Reads mirror __duckRps (live view state + the client's stashed
          // one-shots); challenge/accept/decline route through the SAME client calls the UI uses
          // (server-authoritative throughout). `setLevelOverride` pins the sampler's mic reading —
          // Chromium's fake mic is a constant tone, so a scripted battle needs a scripted loudness.
          // Never used by product logic — observability only.
          (window as unknown as { __duckBark?: unknown }).__duckBark = () => ({
            match: barkMatchRef.current
              ? {
                  matchId: barkMatchRef.current.matchId,
                  phase: barkMatchRef.current.phase,
                  opponentName: barkMatchRef.current.opponentName,
                }
              : null,
            lastInvite: clientRef.current?.lastBarkInvite
              ? {
                  matchId: clientRef.current.lastBarkInvite.matchId,
                  fromSessionId: clientRef.current.lastBarkInvite.fromSessionId,
                  fromName: clientRef.current.lastBarkInvite.fromName,
                }
              : null,
            lastTick: clientRef.current?.lastBarkTick
              ? {
                  matchId: clientRef.current.lastBarkTick.matchId,
                  remainingMs: clientRef.current.lastBarkTick.remainingMs,
                  yourScore: clientRef.current.lastBarkTick.yourScore,
                  theirScore: clientRef.current.lastBarkTick.theirScore,
                }
              : null,
            lastResult: clientRef.current?.lastBarkResult
              ? {
                  matchId: clientRef.current.lastBarkResult.matchId,
                  outcome: clientRef.current.lastBarkResult.outcome,
                  opponentName: clientRef.current.lastBarkResult.opponentName,
                  yourScore: clientRef.current.lastBarkResult.yourScore ?? null,
                  theirScore: clientRef.current.lastBarkResult.theirScore ?? null,
                }
              : null,
            micLevel: voiceRef.current?.readMicLevelNow() ?? 0,
            sentSamples: barkSentCountRef.current,
            challenge: (targetSessionId: string) =>
              clientRef.current?.sendBarkChallenge(targetSessionId),
            accept: (matchId: string) => handleBarkAccept(matchId),
            decline: (matchId: string) => handleBarkDecline(matchId),
            setLevelOverride: (level: number | null) => {
              barkLevelOverrideRef.current = level;
            },
          });
          // Task 6b "Sound reactions": dev-only observability + driver for the e2e. `lastPlayed`/
          // `playedCount`/`catalog` read off the soundboard-audio module's state (what actually played, post-
          // autoplay/concurrency gating); `lastReceived` is the last SOUND_REACTED the client saw; `send`
          // routes through the SAME client call the UI uses (server-authoritative). Observability only.
          (window as unknown as { __duckSound?: unknown }).__duckSound = () => {
            const dbg = soundReactionDebug();
            const recv = clientRef.current?.lastSoundReacted ?? null;
            return {
              lastPlayed: dbg.lastPlayed,
              playedCount: dbg.playedCount,
              catalog: dbg.catalog,
              lastReceived: recv ? { sessionId: recv.sessionId, sound: recv.sound } : null,
              send: (sound: SoundReactionId) => {
                unlockSoundAudio();
                clientRef.current?.sendSoundReact(sound);
              },
            };
          };
          // ADR-0033 "Media Hub": dev-only observability + driver for the e2e (never used by product
          // logic). `zoneId` is the zone I'm reading; `state` is the hub for that zone; `player` is the
          // local IFrame snapshot; `clockOffsetMs` is the sync offset; `screen` reports my own share + the
          // sessionIds whose screen I receive; `send` routes through the SAME client call the UI uses (the
          // server re-checks host/owner for control). Lets the e2e assert two clients converge on the hub.
          (window as unknown as { __duckMedia?: unknown }).__duckMedia = () => {
            const zoneId = currentZoneIdRef.current;
            const v = voiceRef.current?.debugSnapshot();
            return {
              zoneId,
              state: zoneId ? (clientRef.current?.media(zoneId) ?? null) : null,
              clockOffsetMs: clientRef.current?.mediaClockOffset() ?? 0,
              player: radioDebug(),
              screen: {
                status: voiceRef.current?.getScreenStatus() ?? 'off',
                peers: v?.screenPeers ?? [],
              },
              lastReaction: clientRef.current?.lastMediaReacted
                ? {
                    zoneId: clientRef.current.lastMediaReacted.zoneId,
                    sessionId: clientRef.current.lastMediaReacted.sessionId,
                    reaction: clientRef.current.lastMediaReacted.reaction,
                  }
                : null,
              send: (msg: MediaControlMessage) => clientRef.current?.sendMedia(msg),
              share: () => voiceRef.current?.enableScreenshare(),
              stopShare: () => voiceRef.current?.disableScreenshare(),
            };
          };
          // Webcam "show your face" (ADR-0030): dev-only observability + drivers for the e2e. Reports my
          // camera status, the sessionIds whose faces I currently RECEIVE (proximity-gated), and my pin;
          // plus drivers to enable/disable my camera and pin/unpin a peer — the SAME calls the UI uses.
          (window as unknown as { __duckCamera?: unknown }).__duckCamera = () => ({
            cameraStatus: voiceRef.current?.getCameraStatus() ?? 'off',
            videoPeers: voiceRef.current?.debugSnapshot().videoPeers ?? [],
            pinned: pinnedRef.current,
            videoCodec: voiceRef.current?.debugSnapshot().videoCodec ?? null,
            enableCamera: () => voiceRef.current?.enableCamera(),
            disableCamera: () => voiceRef.current?.disableCamera(),
            pin: (sessionId: string | null) => {
              if (!sessionId || sessionId === clientRef.current?.selfSessionId) return;
              setPinnedSessionIds((cur) =>
                cur.includes(sessionId)
                  ? cur.filter((s) => s !== sessionId)
                  : [...cur, sessionId].slice(-MAX_PINNED_CAMERAS),
              );
            },
            // Does my OWN head bubble actually PAINT live video (visible + the texture has frames)? Lets the
            // e2e guard the RENDER path, not just the data path — the empty-bubble bug hid behind data.
            selfRenderState: () =>
              engineRef.current?.webcamRenderState(clientRef.current?.selfSessionId ?? '') ?? null,
            renderState: (sessionId: string) =>
              engineRef.current?.webcamRenderState(sessionId) ?? null,
          });
          // Entryway front door (ADR-0022): dev-only observability + drivers for the e2e. Reports the door
          // open/closed state + the last bell ring / knock the client saw + what the entryway audio module
          // actually played (post autoplay/concurrency gating); the ring/knock/open drivers route through the
          // SAME client calls the UI uses (server-authoritative — it re-checks position/side/host). Read-only.
          (window as unknown as { __duckEntry?: unknown }).__duckEntry = () => {
            const c = clientRef.current;
            return {
              doorOpen: c?.entryDoorOpen() ?? false,
              lastBellRang: c?.lastBellRang ?? null,
              lastKnocked: c?.lastKnocked ?? null,
              audio: entrywayAudioDebug(),
              ring: () => {
                unlockEntrywayAudio();
                clientRef.current?.ringBell();
              },
              knock: () => {
                unlockEntrywayAudio();
                clientRef.current?.knock();
              },
              open: () => clientRef.current?.openDoor(),
            };
          };
          // Room Doors: dev-only observability + drivers for the e2e (never used by product logic). Reports
          // the closed interior doors + the nearest door action the local duck sees; `toggle` routes through
          // the SAME client call the UI uses (server-authoritative — it re-checks position/host). Read-only.
          (window as unknown as { __duckDoors?: unknown }).__duckDoors = () => {
            const c = clientRef.current;
            return {
              closed: c?.closedDoorIds() ?? [],
              entryOpen: c?.entryDoorOpen() ?? false,
              // Door HOLDS (ADR-0060): doorway id → the holder's userId ('' = unheld soft close).
              holds: Object.fromEntries(c?.doorHolds() ?? []),
              nearest: engineRef.current?.currentDoorAction() ?? null,
              toggle: (doorwayId: string, open: boolean) => c?.toggleDoor(doorwayId, open),
            };
          };
          // Task 2 "interactive SIT + HOLD ITEMS": dev-only observability + drivers for the e2e (never used
          // by product logic). `self` reports MY synced seat/held; `players` exposes EVERY duck's synced
          // `seatedAt`/`heldItem` (canvas-drawn, so invisible to DOM queries) — the e2e asserts off this,
          // mirroring how `__duckWorld().players` exposes presence/cosmetics. `sit`/`setHeld` route through
          // the SAME client calls the UI uses (server-authoritative throughout). `seats` lists the nearest
          // seat + whether it's free, so the e2e can steer to a seat deterministically.
          (window as unknown as { __duckSeats?: unknown }).__duckSeats = () => {
            const c = clientRef.current;
            const self = c?.self();
            const players: Record<string, { seatedAt: string; heldItem: string }> = {};
            if (c) {
              for (const [sid, p] of c.players) {
                players[sid] = { seatedAt: p.seatedAt ?? '', heldItem: p.heldItem ?? '' };
              }
            }
            const near = self ? nearestSeatIn(resolveLevelInto(levelRef, clientRef.current), self.x, self.y) : null;
            return {
              self: self ? { seatedAt: self.seatedAt ?? '', heldItem: self.heldItem ?? '' } : null,
              players,
              nearestSeatId: near?.id ?? null,
              sit: (seatId: string) => clientRef.current?.sit(seatId),
              sitNearest: () => {
                const s = clientRef.current?.self();
                if (!s) return null;
                if (s.seatedAt) {
                  clientRef.current?.sit(s.seatedAt);
                  return s.seatedAt;
                }
                const seat = nearestSeatIn(resolveLevelInto(levelRef, clientRef.current), s.x, s.y);
                if (seat) clientRef.current?.sit(seat.id);
                return seat?.id ?? null;
              },
              setHeld: (item: string) => clientRef.current?.setHeld(item),
              rawMove: (dx: number, dy: number) => {
                const cc = clientRef.current as unknown as { __dbgSeq?: number; sendMove: (s: number, x: number, y: number) => void };
                cc.__dbgSeq = (cc.__dbgSeq ?? 9_000_000) + 1;
                cc.sendMove(cc.__dbgSeq, dx, dy);
              },
            };
          };
          // Gamified-XP redesign: dev-only observability + driver for the e2e (never used by product
          // logic). `lastQuestFired` is the last QUEST_FIRED the client saw (quest id + xp); `meeting` is
          // the live room-wide meeting progress (seated/phase/timers); `sendWork` routes through the SAME
          // client call the Work prompt uses (server-authoritative — seated-at-a-desk + cooldown enforced
          // server-side). Lets the e2e drive a WORK beat + assert the toast/meeting bar deterministically.
          (window as unknown as { __duckQuests?: unknown }).__duckQuests = () => {
            const c = clientRef.current;
            const last = c?.lastQuestFired ?? null;
            return {
              lastQuestFired: last ? { quest: last.quest, xp: last.xp } : null,
              meeting: c ? c.meeting() : { seated: 0, phase: 'idle', elapsedSec: 0, targetSec: 0 },
              sendWork: () => clientRef.current?.sendWork(),
            };
          };
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to connect to the office');
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      // Voice first: stop the mic + close transports/AudioContext before the room socket goes away.
      voiceRef.current?.stop();
      voiceRef.current = null;
      clientRef.current = null;
      // Deliberate teardown: leave() sets the intentional-leave guard so the terminal onLeave is a clean
      // exit (never a `lost` alarm) and disables the SDK's grace retries (M3.5b).
      void client?.leave();
      if (import.meta.env?.DEV) {
        delete (window as unknown as { __duckNet?: unknown }).__duckNet;
        delete (window as unknown as { __duckSetCosmetics?: unknown }).__duckSetCosmetics;
        delete (window as unknown as { __duckRps?: unknown }).__duckRps;
        delete (window as unknown as { __duckBark?: unknown }).__duckBark;
        delete (window as unknown as { __duckSound?: unknown }).__duckSound;
        delete (window as unknown as { __duckEntry?: unknown }).__duckEntry;
        delete (window as unknown as { __duckSeats?: unknown }).__duckSeats;
        delete (window as unknown as { __duckQuests?: unknown }).__duckQuests;
        delete (window as unknown as { __duckMedia?: unknown }).__duckMedia;
        delete (window as unknown as { __duckCamera?: unknown }).__duckCamera;
        delete (window as unknown as { __duckRenders?: unknown }).__duckRenders;
      }
    };
    // This is a once-per-session dev-hook SETUP effect: it installs the `__duck*` e2e hooks (which read
    // their inputs lazily off refs at call time) and re-runs only when the room identity changes. The
    // notice/quest/chat helpers it references are stable enough that re-binding on each is undesirable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUserId, sessionWorkspaceId]);

  // Deep-linkable per-room URLs (ADR-0041): the URL is the SOURCE OF TRUTH for the active room, so the
  // address bar must always hold the canonical `/{level_type}/{room_slug}`. This effect MIRRORS the live
  // (theme, room) onto the URL — it NEVER drives a join (the connect effect above owns the single, stable
  // session and is deliberately keyed only on the identity, so canonicalizing the URL here can never
  // re-join / re-create the Pixi+voice session — the FPS/“session reload” bug). It runs once we're
  // connected and the workspace (slug/id) is known, and any time the live theme changes (an owner's world
  // re-skin shifts the level_type segment without a reload) or the path changes (Back/Forward/deep link):
  //   • bare `/office` (the legacy entrypoint the auth pages still target) → replace with the canonical path;
  //   • a deep link that resolves to MY workspace (slug OR id, any level_type segment) → rewrite to the
  //     canonical slug form (so an id-link or a stale theme segment self-heals);
  //   • a foreign/mismatched room key (the cross-workspace case we scope out) → gracefully send the player
  //     to THEIR default room (matches “переход в комнату по умолчанию”), never a dead end.
  // `replace:true` keeps Back working naturally (we don't pollute history with the canonicalization hop).
  const wsSlug = workspace.data?.slug ?? null;
  const wsId = workspace.data?.id ?? null;
  useEffect(() => {
    if (!connected || !wsSlug || !wsId) return;
    const canonical = buildRoomPath(worldThemeId, wsSlug);
    if (location.pathname !== canonical) navigate(canonical, { replace: true });
    // Re-evaluate when the room identity, the live theme (owner re-skin), OR the address bar changes.
  }, [connected, wsSlug, wsId, worldThemeId, location.pathname, navigate]);

  // Poll the realtime ping for the HUD (cheap, 1 Hz — not on the render hot path). Also reconcile the
  // elected presence from the authoritative schema, so an object interaction that SET our presence
  // server-side (M4) is reflected in the picker even though we didn't choose it via the picker.
  useEffect(() => {
    const id = setInterval(() => {
      const c = clientRef.current;
      if (c) {
        setPlayerCount(c.players.size);
        const self = c.self();
        if (self) setMyPresence((prev) => (prev === self.presence ? prev : (self.presence as PresenceFlavor)));
        // Refresh the shared sky for the HUD's dual-coded ambient indicator (M4.2). Cheap scalar reads;
        // the setters bail when unchanged so we don't re-render the HUD every second for a static sky.
        const sky = c.ambient();
        setTimeOfDay((prev) => (prev === sky.timeOfDay ? prev : sky.timeOfDay));
        setWeather((prev) => (prev === sky.weather ? prev : sky.weather));
        // M7b: refresh the shared world event for the HUD banner. Cheap scalar read; the setter bails
        // when unchanged so a steady event (or 'none') never re-renders the HUD every second.
        const event = c.worldEvent();
        setWorldEvent((prev) => (prev === event ? prev : event));
        // M8: refresh the guild's world THEME for the HUD's world-name chip + the owner picker. Cheap
        // scalar read; the setter bails when unchanged so a steady theme never re-renders the HUD. An
        // owner's switch broadcasts on room state, so this picks it up for everyone within ~1s (the
        // engine re-skins the canvas on its own per-frame read).
        const theme = c.theme();
        setWorldThemeId((prev) => (prev === theme ? prev : theme));
        // Gamified-XP: refresh the shared MEETING progress for the HUD bar. The bar ticks its own timer
        // off elapsed/target, so 1 Hz is plenty; the setter only re-renders when a field actually changes
        // (so a steady idle/in-session phase doesn't re-render the HUD every second).
        const m = c.meeting();
        setMeeting((prev) =>
          prev.seated === m.seated &&
          prev.phase === m.phase &&
          prev.elapsedSec === m.elapsedSec &&
          prev.targetSec === m.targetSec
            ? prev
            : m,
        );
        // ADR-0033: refresh the MEDIA HUB for the zone I'm standing in (the card extrapolates position
        // itself). The setter bails when nothing changed so a steady item never re-renders every second.
        const zoneId = currentZoneIdRef.current;
        const h = zoneId ? c.media(zoneId) : null;
        setMediaHub((prev) => (mediaHubEqual(prev, h) ? prev : h));
        const off = c.mediaClockOffset();
        setMediaClockOffsetMs((prev) => (Math.abs(prev - off) < 1 ? prev : off));
        // Resolve the now-playing screen-share stream (mine via local capture, a peer's via the SFU).
        const voice = voiceRef.current;
        const selfSid = c.selfSessionId;
        const nextStream =
          h?.now?.kind === 'screenshare'
            ? h.now.ref === selfSid
              ? (voice?.getLocalScreenStream() ?? null)
              : (voice?.getPeerScreenStream(h.now.ref) ?? null)
            : null;
        setMediaScreenStream((prev) => (prev === nextStream ? prev : nextStream));
        // ADR-0034 adaptive bitrate: while I'M the one screen-sharing, re-tune the outgoing cap to the
        // current audience size (the SFU fans my one stream to everyone in the zone). setScreenMaxBitrate
        // no-ops unless the tier actually changed, so this 1 Hz check is cheap.
        if (voice?.isScreenSharing() && h?.now?.kind === 'screenshare' && h.now.ref === selfSid) {
          voice.setScreenMaxBitrate(screenshareMaxBitrate(zoneViewerCount()));
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, [zoneViewerCount]);

  // Task 2 "interactive SIT": a faster (≈5 Hz) reconcile loop for the seat prompt — responsive enough
  // that the "Sit" / "Stand up" affordance appears as you walk up to / onto a seat, but well off the
  // render hot path. Reads the AUTHORITATIVE self position + `seatedAt`/`heldItem` (the server owns them):
  //   • mySeatedAt — drives "Stand up" + makes E a toggle (seated → stand). Reconciled here so a seat I
  //     was bumped off (e.g. by walking) updates the HUD even though I didn't toggle it.
  //   • nearestSeat — the closest empty seat (skipping a seat someone else is on); drives the "Sit" prompt.
  //   • myHeldItem — reconciled so the picker reflects the synced prop. Every setter bails when unchanged.
  useEffect(() => {
    const id = setInterval(() => {
      // cost-census: the sit/work prompt is visual — skip the 5Hz O(N) seat-occupancy scan while backgrounded.
      if (isTabHidden()) return;
      const c = clientRef.current;
      const self = c?.self();
      if (!c || !self) return;
      setMySeatedAt((prev) => (prev === self.seatedAt ? prev : self.seatedAt));
      setMyHeldItem((prev) => (prev === self.heldItem ? prev : self.heldItem));
      // Keep the React-side level id in sync (Phase 3) so the host Doors panel lists the right world's doors
      // even if no door is ever toggled (handleDoorsChange would otherwise be the only writer).
      const lid = c.levelId();
      setLevelId((prev) => (prev === lid ? prev : lid));
      // Gamified-XP: am I seated at a WORKSTATION desk? Drives the repeatable "Work" prompt. Derived from
      // the authoritative seat id; clears the instant I stand or move to a non-desk seat.
      const onDesk = self.seatedAt !== '' && resolveLevelInto(levelRef, clientRef.current).workstationSeatIds.includes(self.seatedAt);
      setAtWorkstation((prev) => (prev === onDesk ? prev : onDesk));
      // Only show a "Sit" prompt when standing; resolve the nearest seat that isn't already taken.
      let seat: Seat | null = null;
      if (!self.seatedAt && !bareLevelFor(resolveLevelInto(levelRef, clientRef.current).id)) {
        const near = nearestSeatIn(resolveLevelInto(levelRef, clientRef.current), self.x, self.y);
        if (near) {
          let taken = false;
          for (const [, p] of c.players) {
            if (p.seatedAt === near.id) {
              taken = true;
              break;
            }
          }
          if (!taken) seat = near;
        }
      }
      setNearestSeatState((prev) => (prev?.id === (seat?.id ?? null) ? prev : seat));
    }, 200);
    return () => clearInterval(id);
  }, []);

  // M6: mirror the gateway-attested coin balance onto the engine so the dev `__duckWorld()` snapshot
  // can expose `coins` for the e2e (coins are NOT on the realtime schema). Render/product logic never
  // reads this — it's observability only.
  useEffect(() => {
    if (engineRef.current) engineRef.current.selfCoins = progression.data?.coins ?? null;
  }, [progression.data?.coins]);

  // M7b-2: clear the transient RPS result auto-clear timer on unmount so a pending setTimeout can't fire
  // after teardown (no stray state update / leak).
  useEffect(() => {
    return () => {
      if (rpsResultTimer.current) clearTimeout(rpsResultTimer.current);
      // Gamified-XP: also clear the pending quest-toast flush so it can't fire after teardown.
      if (questToastTimer.current) clearTimeout(questToastTimer.current);
    };
  }, []);

  // Push-to-talk key (M3.3): hold V to talk, release to mute. The VoiceClient no-ops these unless PTT
  // mode is on, so the listener is always safe to keep mounted. Auto-repeat is ignored (e.repeat), and
  // we don't hijack V while typing in a form field (the device picker, future chat, etc.).
  useEffect(() => {
    const isTyping = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null;
      if (!node) return false;
      const tag = node.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Match the PHYSICAL V key so push-to-talk works on any layout (physicalKey()), not just US QWERTY.
      if (e.repeat || physicalKey(e) !== 'v' || isTyping(e.target)) return;
      voiceRef.current?.setPttTalking(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      // Same resolution as keydown: typing in a form field must not clear the talk latch (premature mute).
      if (physicalKey(e) !== 'v' || isTyping(e.target)) return;
      voiceRef.current?.setPttTalking(false);
    };
    // Safety: if focus/visibility is lost while holding V, drop the talk latch so the mic re-mutes.
    const onBlur = () => voiceRef.current?.setPttTalking(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Interact key (M4 + Task 2): press E to SIT/STAND or use the nearest object. Window-level so it works
  // regardless of focus, ignored while typing in a form field + on auto-repeat (mirrors PTT). The engine
  // owns object proximity; seating is layered on top here (the engine is frozen for the room-design pass),
  // with a clear precedence so E never double-fires:
  //   1. seated  → STAND UP (toggle the seat I'm on),
  //   2. a seat in range AND no object in range OR the seat is closer than the object → SIT,
  //   3. otherwise → use the nearest object (the existing M4 path).
  // The server re-checks proximity + occupancy for every branch, so the local precedence is just UX.
  useEffect(() => {
    const isTyping = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null;
      if (!node) return false;
      const tag = node.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Match the PHYSICAL E key so interact works on any layout (physicalKey()), not just US QWERTY.
      if (e.repeat || physicalKey(e) !== 'e' || isTyping(e.target)) return;
      const c = clientRef.current;
      const self = c?.self();
      // 0) Entryway front door (ADR-0022): ring / knock / open takes precedence when in range. There are no
      // seats or objects at the door or bell, so this never steals another affordance — it only ever fires
      // while standing right at the front door, where E should operate the door, not a distant prop.
      if (engineRef.current?.hasEntryAction()) {
        engineRef.current.triggerEntryAction();
        return;
      }
      // 0.5) Interior room door (Room Doors): standing right at a doorway, E opens/closes it. Doorways sit
      // on room thresholds, away from seats/props, so this rarely competes — and when you're at a door, E
      // operating the door is the expectation.
      if (engineRef.current?.hasDoorAction()) {
        engineRef.current.triggerDoorAction();
        return;
      }
      // 1) Seated → stand up (toggle), and don't also interact an object this press.
      if (self?.seatedAt) {
        c?.sit(self.seatedAt);
        return;
      }
      // 2) Prefer a seat when one is in range and the player isn't closer to an interactive object.
      if (self && !bareLevelFor(resolveLevelInto(levelRef, clientRef.current).id)) {
        const seat = nearestSeatIn(resolveLevelInto(levelRef, clientRef.current), self.x, self.y);
        if (seat) {
          let taken = false;
          for (const [, p] of (c?.players ?? [])) {
            if (p.seatedAt === seat.id) {
              taken = true;
              break;
            }
          }
          const obj = nearestObject;
          const seatD = Math.hypot(seat.x - self.x, seat.y - self.y);
          const objD = obj ? Math.hypot(obj.x - self.x, obj.y - self.y) : Infinity;
          if (!taken && seatD <= objD) {
            c?.sit(seat.id);
            return;
          }
        }
      }
      // 3) Fall back to the existing object-interact path.
      engineRef.current?.interactNearest();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // `nearestObject` is read inside the handler to compare distances; re-bind when it changes so the
    // closure never compares against a stale object (the handler is otherwise stable).
  }, [nearestObject]);

  const handleLogout = () => {
    // The user asked to leave — honour that intent FIRST and UNCONDITIONALLY. Dropping the local
    // session and navigating away is the whole job, and it must never be blocked or aborted by a slow
    // network round-trip or a throwing media/socket teardown (e.g. a half-closed WebRTC transport).
    // The old code tore voice/room down BEFORE clearing auth, so any throw there left the player signed
    // in — and since we never navigated, the unmount cleanup that would have done the teardown never
    // ran either. That dead-end is why "Log out" sometimes did nothing and people cleared cookies.
    const refreshToken = tokens?.refreshToken;
    logoutSession();
    // Guest-first home: RequireAuth and the catch-all both funnel logged-out visitors to /register, so
    // that's the canonical "you're out, here's the way back" screen (it links to /login for the rare
    // email account). /login would be a dead end for the guest majority who have no password.
    navigate('/register');
    // Best-effort, fire-and-forget cleanup AFTER the user is already out. Navigating away unmounts
    // OfficePage, whose own effect cleanup stops voice + leaves the room — so all that's left here is
    // telling the server to revoke this device's refresh token. A failure can't strand the session.
    if (refreshToken) void api.logout(refreshToken).catch(() => undefined);
  };

  // M3.4: the peers I currently hear, EXCLUDING anyone I've blocked (they may briefly linger in the
  // audible list until the server's PRODUCER_CLOSED lands — filtering keeps the "People nearby" list
  // honest the instant I block). Blocks are keyed by STABLE userId, so resolve each audible peer's
  // sessionId→userId via the roster before testing membership.
  const roster = clientRef.current?.players;
  const nearbyPeers = audiblePeers.filter((p) => {
    const userId = roster?.get(p.sessionId)?.userId;
    return !userId || !blockedUserIds.has(userId);
  });
  // The session ids I can currently HEAR (already excludes blocked) — the participant rows that get an
  // inline voice-Block control. Blocking only makes sense for someone you can actually hear.
  const audibleSessionIds = new Set(nearbyPeers.map((p) => p.sessionId));

  // The "Blocked" list maps each blocked userId to its CURRENT roster entry (live sessionId + name).
  // When the citizen is OFFLINE (not in the roster) we keep them blocked but mark them offline — the
  // HUD disables Unblock for them (no live session to send the lift to) and shows a gentle hint. Names
  // for present peers come from the live roster; offline names fall back to the remembered name.
  const presentByUserId = new Map<string, { sessionId: string; name: string }>();
  if (roster) {
    for (const [sid, p] of roster) {
      if (blockedUserIds.has(p.userId)) presentByUserId.set(p.userId, { sessionId: sid, name: p.displayName });
    }
  }
  const blockedPeers = [...blockedUserIds].map((userId) => {
    const present = presentByUserId.get(userId);
    return present
      ? { userId, sessionId: present.sessionId, name: present.name, online: true }
      : {
          userId,
          sessionId: null,
          name: blockedNamesRef.current.get(userId) ?? 'A blocked duck',
          online: false,
        };
  });

  if (!user) return <CenteredMessage>Loading your duck…</CenteredMessage>;
  if (status === 'error') {
    return (
      <CenteredMessage>
        <p className="text-red-300">Couldn’t join the office.</p>
        <p className="mt-1 text-sm text-white/60">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-xl bg-amber-400 px-4 py-2 font-semibold text-slate-900"
        >
          Retry
        </button>
      </CenteredMessage>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      {status === 'connected' && clientRef.current ? (
        <>
          <OfficeView
            client={clientRef.current}
            isHost={user.role === 'OWNER'}
            onGiveReputation={handleGiveReputation}
            onChallengeNearest={handleEngineChallenge}
            onInteract={handleInteract}
            onNearestObjectChange={handleNearestObjectChange}
            onNearestEntryActionChange={handleNearestEntryActionChange}
            onEntryAction={handleEntryAction}
            onWebcamClick={handlePinCamera}
            onNearestDoorActionChange={handleNearestDoorActionChange}
            onDoorAction={handleDoorAction}
            onDoorsChange={handleDoorsChange}
            onEngineReady={(engine) => {
              engineRef.current = engine;
              // Seed the engine's coin mirror immediately (the effect only fires on a coins CHANGE, so a
              // late-arriving engine would otherwise miss the already-loaded balance) — M6 observability.
              if (engine) {
                engine.selfCoins = progression.data?.coins ?? null;
                // Furniture/object CONTEXT MENU (right-click / long-press): the engine hands us what's
                // under the cursor; we render the menu (state setter is stable, so this binds once).
                engine.onContextMenu = (req) => setCtxMenu(req);
              }
            }}
          />
          <HUD
            user={user}
            playerCount={playerCount}
            netStatus={netStatus}
            progression={progression.data}
            guild={guild.data}
            notice={notice}
            voiceStatus={voiceStatus}
            voiceMuted={voiceMuted}
            cameraStatus={cameraStatus}
            currentZone={currentZone}
            pttEnabled={pttEnabled}
            micDevices={micDevices}
            currentDeviceId={currentDeviceId}
            speakerDevices={speakerDevices}
            currentSinkId={currentSinkId}
            roomSections={roomSections}
            audibleSessionIds={audibleSessionIds}
            blockedPeers={blockedPeers}
            voiceAnnouncement={voiceA11yAnnouncement}
            guildPanel={guildPanel}
            chatLines={chatLines}
            rpsInvite={rpsInvite}
            rpsMatch={rpsMatch}
            rpsResult={rpsResult}
            barkInvite={barkInvite}
            barkMatch={barkMatch}
            barkResult={barkResult}
            onEmote={(a: Animation) => clientRef.current?.sendEmote(a)}
            onToggleGuildPanel={handleToggleGuildPanel}
            onCloseGuildPanel={handleCloseGuildPanel}
            onShipRelease={handleShipRelease}
            onNotice={flashNotice}
            onEnableVoice={handleEnableVoice}
            onToggleMute={handleToggleMute}
            onToggleCamera={handleToggleCamera}
            onTogglePtt={handleTogglePtt}
            onSelectMic={handleSelectMic}
            onSelectSpeaker={handleSelectSpeaker}
            onBlockCitizen={handleBlockCitizen}
            onUnblockCitizen={handleUnblockCitizen}
            myPresence={myPresence}
            nearestObject={nearestObject}
            nearestSeat={nearestSeatState}
            nearestEntryAction={nearestEntryAction}
            nearestDoorAction={nearestDoorAction}
            isHost={user.role === 'OWNER'}
            doorState={doorState}
            doorways={LEVELS[levelId].doorways}
            onDoorActionNearest={handleDoorActionNearest}
            onHostToggleDoor={handleHostToggleDoor}
            mySeatedAt={mySeatedAt}
            atWorkstation={atWorkstation}
            myHeldItem={myHeldItem}
            meeting={meeting}
            timeOfDay={timeOfDay}
            weather={weather}
            worldEvent={worldEvent}
            worldThemeId={worldThemeId}
            onSetWorldTheme={handleSetWorldTheme}
            onSetPresence={handleSetPresence}
            onInteractNearest={handleInteractNearest}
            onEntryAction={handleEntryActionNearest}
            onSitNearest={handleSitNearest}
            onWork={handleWork}
            onHoldItem={handleHoldItem}
            onLookChange={handleLookChange}
            onSay={handleSay}
            onReact={handleReact}
            onSoundReact={handleSoundReact}
            onSoundPrefetch={prefetchSoundReaction}
            onChallengeNearest={handleChallengeNearest}
            onRpsChoose={handleRpsChoose}
            onRpsDecline={handleRpsDecline}
            onDismissRpsResult={handleDismissRpsResult}
            onBarkAccept={handleBarkAccept}
            onBarkDecline={handleBarkDecline}
            onDismissBarkResult={handleDismissBarkResult}
            onCapturePhoto={handleCapturePhoto}
            onArmFurniture={handleArmFurniture}
            onLogout={handleLogout}
          />
          {/* Right-click / long-press context menu over the world: Interact (objects) · Pick up & move /
              Rotate / Remove (your furniture) — all server-authoritative (ТЗ). */}
          <FurnitureContextMenu
            request={ctxMenu}
            onClose={() => setCtxMenu(null)}
            onInteract={handleInteract}
            onSit={handleCtxSit}
            onTake={handleCtxTake}
            onRotate={handleCtxRotate}
            onRemove={handleCtxRemove}
          />
          {/* Top-left MEDIA SLOT. Pinned webcams (ADR-0030) take the prime corner — up to a small "huddle"
              of MAX_PINNED_CAMERAS — and the radio (ADR-0021) YIELDS, collapsing below them; the music keeps
              playing and the ToS-required visible mini-player stays mounted. With no pin, the radio holds the
              corner in full. The wrapper is click-through; each card re-enables its own pointer events. */}
          <div className="pointer-events-none absolute left-2 top-2 z-10 flex w-44 flex-col gap-2 sm:left-3 sm:top-3 md:left-4 md:top-4 md:w-64">
            {pinnedSessionIds.map((sid) => (
              <PinnedCameraCard
                key={sid}
                name={clientRef.current?.players.get(sid)?.displayName ?? 'A nearby duck'}
                stream={pinnedStreams[sid] ?? null}
                onClose={() => handleUnpinCamera(sid)}
              />
            ))}
            <MediaHubCard
              hub={mediaHub}
              zoneName={currentZone?.name ?? 'the floor'}
              clockOffsetMs={mediaClockOffsetMs}
              selfSessionId={clientRef.current?.selfSessionId ?? ''}
              isOwner={user.role === 'OWNER'}
              screenStream={mediaScreenStream}
              localScreenStatus={localScreenStatus}
              onControl={handleMediaControl}
              onToggleShare={handleToggleShare}
              reactionTick={mediaReaction}
              collapsed={pinnedSessionIds.length > 0}
            />
          </div>
          {/* Self-preview while sharing your face (ADR-0030): always-on proof of exactly what nearby ducks
              see (mirrored) + a one-click kill switch. Renders nothing when the camera is off. */}
          <SelfCameraPreview
            stream={selfCameraStream}
            onStop={() => voiceRef.current?.disableCamera()}
          />
          {/* One-time consent primer, shown before the very first browser camera prompt. */}
          {showCameraPrimer && (
            <CameraPrimer
              onConfirm={handleConfirmCameraPrimer}
              onDismiss={() => setShowCameraPrimer(false)}
            />
          )}
          {/* First-run welcome / onboarding (Task 4) — self-gating via localStorage, greets once arrived. */}
          <WelcomeModal />
        </>
      ) : (
        <CenteredMessage>Walking into the office…</CenteredMessage>
      )}
    </div>
  );
}

/** Mounts the Pixi engine against a connected realtime client and tears it down on unmount. */
function OfficeView({
  client,
  isHost,
  onGiveReputation,
  onChallengeNearest,
  onInteract,
  onNearestObjectChange,
  onNearestEntryActionChange,
  onEntryAction,
  onWebcamClick,
  onNearestDoorActionChange,
  onDoorAction,
  onDoorsChange,
  onEngineReady,
}: {
  client: RealtimeClient;
  /** Whether the local player is the session HOST (workspace OWNER) — decides the entryway open/knock prompt. */
  isHost: boolean;
  onGiveReputation: (targetUserId: string, targetSessionId: string) => void;
  /** Called when the player challenges the nearest duck to a minigame (M7b-2 RPS / Bark Battle). */
  onChallengeNearest: (targetUserId: string, targetSessionId: string, game: ChallengeGame) => void;
  /** Called when the player uses (interacts with) a nearby world object (M4). */
  onInteract: (objectId: string) => void;
  /** Called (on change) with the nearest interactable object, or null when out of range (M4). */
  onNearestObjectChange: (object: InteractiveObject | null) => void;
  /** Called (on change) with the nearest entryway front-door action, or null when none is in range (ADR-0022). */
  onNearestEntryActionChange: (action: EntryActionView | null) => void;
  /** Called when the player performs a front-door action (ring/knock/open) via the engine (ADR-0022). */
  onEntryAction: (kind: EntryActionKind) => void;
  /** Called when the player taps a peer's webcam bubble in the world — pin that camera (ADR-0030). */
  onWebcamClick: (targetSessionId: string) => void;
  /** Called (on change) with the nearest interior-door action, or null when none is in range (Room Doors). */
  onNearestDoorActionChange: (action: DoorActionView | null) => void;
  /** Called when the player toggles an interior door via the engine (Room Doors). */
  onDoorAction: (doorwayId: string, open: boolean, locked?: boolean) => void;
  /** Called (on change) with the whole door state so the host's Doors panel reflects it live (Room Doors). */
  onDoorsChange: (closedDoorIds: string[], entryDoorOpen: boolean, heldDoorIds: string[]) => void;
  /** Publishes the live engine (and `null` on teardown) so voice can drive the speaking indicator. */
  onEngineReady: (engine: OfficeEngine | null) => void;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the latest callbacks in refs so the engine effect only depends on `client`
  // (re-running it would tear down and rebuild the Pixi app on every render).
  const giveRef = useRef(onGiveReputation);
  giveRef.current = onGiveReputation;
  const challengeRef = useRef(onChallengeNearest);
  challengeRef.current = onChallengeNearest;
  const interactRef = useRef(onInteract);
  interactRef.current = onInteract;
  const nearestRef = useRef(onNearestObjectChange);
  nearestRef.current = onNearestObjectChange;
  const nearestEntryRef = useRef(onNearestEntryActionChange);
  nearestEntryRef.current = onNearestEntryActionChange;
  const entryActionRef = useRef(onEntryAction);
  entryActionRef.current = onEntryAction;
  const webcamClickRef = useRef(onWebcamClick);
  webcamClickRef.current = onWebcamClick;
  const nearestDoorRef = useRef(onNearestDoorActionChange);
  nearestDoorRef.current = onNearestDoorActionChange;
  const doorActionRef = useRef(onDoorAction);
  doorActionRef.current = onDoorAction;
  const doorsChangeRef = useRef(onDoorsChange);
  doorsChangeRef.current = onDoorsChange;
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;
  const readyRef = useRef(onEngineReady);
  readyRef.current = onEngineReady;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const engine = new OfficeEngine(el, client);
    engine.onGiveReputation = (uid, sid) => giveRef.current(uid, sid);
    engine.onChallengeNearest = (uid, sid, game) => challengeRef.current(uid, sid, game);
    engine.onInteract = (objectId) => interactRef.current(objectId);
    // Furniture Store: a confirmed placement (click on the world while a piece is armed) → ask the server
    // to buy & place it. The server owns the price/balance/cap; the verdict comes back via onFurnitureResult.
    engine.onPlaceFurniture = (itemId, x, y) => client.placeFurniture(itemId, x, y);
    engine.onNearestObjectChange = (object) => nearestRef.current(object);
    engine.onNearestEntryActionChange = (action) => nearestEntryRef.current(action);
    engine.onEntryAction = (kind) => entryActionRef.current(kind);
    engine.onWebcamClick = (sid) => webcamClickRef.current(sid);
    engine.onNearestDoorActionChange = (action) => nearestDoorRef.current(action);
    engine.onDoorAction = (doorwayId, open, locked) => doorActionRef.current(doorwayId, open, locked);
    engine.onDoorsChange = (closedDoorIds, entryDoorOpen, heldDoorIds) =>
      doorsChangeRef.current(closedDoorIds, entryDoorOpen, heldDoorIds);
    engine.isHost = isHostRef.current;
    void engine.init();
    readyRef.current(engine);
    return () => {
      readyRef.current(null);
      engine.destroy();
    };
  }, [client]);

  return <div ref={ref} className="absolute inset-0" />;
}

function CenteredMessage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center text-lg text-white/80"
      >
        <div className="mb-3 text-5xl">🦆</div>
        {children}
      </motion.div>
    </div>
  );
}
